import Phaser from "phaser";
import type { Enemy } from "./Enemy";
import type { TowerDef } from "../types";
import type { JuiceSystem } from "../systems/JuiceSystem";
import type { Archetype } from "./CasterSprite";

/** Per-tower-id visual override. Each entry maps a tower to a Dota 2 ability silhouette. */
type VisualKind =
  | "default"
  | "arrow"
  | "flame"
  | "tidal_wave"
  | "boulder_rocket"
  | "lightning"
  | "laser_beam"
  | "dual_breath"
  | "storm_hammer"
  | "bone_axe"
  | "snowflake"
  | "vine_whip"
  | "brew_jug"
  | "soul_wisp"
  | "beast_axes"
  | "tentacle"
  | "death_scythe"
  | "phantom_blade"
  | "void_blade"
  | "moon_tornado"
  | "rocket_barrage"
  | "firestorm";
function pickVisualKind(towerId: string): VisualKind {
  // Mirana — Sacred Arrow (silver shaft + steel head + moon-glow halo).
  if (towerId === "nature") return "arrow";
  // Lina — Dragon Slave (angled flame wave, not orb).
  if (towerId === "fire") return "flame";
  // Water — cresting tidal wave crescent.
  if (towerId === "water") return "tidal_wave";
  // Earth — boulder rocket with smoke trail.
  if (towerId === "earth") return "boulder_rocket";
  // Karroch the Beastmaster (lava) — twin spinning Wild Axes signature ability.
  if (towerId === "lava") return "beast_axes";
  // Jakiro (steam) — twin elemental breath projectile (NOT a laser beam).
  if (towerId === "steam") return "dual_breath";
  // Sven (forest_fire) — purple void spinning blade (twin-fan buzzsaw).
  if (towerId === "forest_fire") return "void_blade";
  // Witch Doctor (mud) — spinning bone axe.
  if (towerId === "mud") return "bone_axe";
  // Lich (ice) — six-pointed snowflake shuriken.
  if (towerId === "ice") return "snowflake";
  // Venomancer (vine) — growing thorn whip vine.
  if (towerId === "vine") return "vine_whip";
  // Brewmaster — fire-ale brew jug, splashes flames.
  if (towerId === "brew") return "brew_jug";
  // Zeus (voidmage) — chained lightning bolt.
  if (towerId === "voidmage") return "lightning";
  // Vrogros the Underlord (beast) — Firestorm pillar of fire descending on target.
  if (towerId === "beast") return "firestorm";
  // Tidehunter — Ravage tentacle whip writhing forward.
  if (towerId === "tide") return "tentacle";
  // Necrolyte — spinning death scythe blade.
  if (towerId === "necro") return "death_scythe";
  // Phantom Assassin — long sword cleave that flies forward.
  if (towerId === "phantom") return "phantom_blade";
  // Luna — laser beam wrapped in golden tornado swirl.
  if (towerId === "luna") return "moon_tornado";
  // Tinker — homing plasma rocket missile.
  if (towerId === "tinker") return "rocket_barrage";
  return "default";
}

/** A homing spell projectile with shape that depends on the firing hero's archetype. */
export class Projectile {
  readonly visual: Phaser.GameObjects.Container;
  alive = true;
  private speed: number;
  private trailAccum = 0;
  private visualKind: VisualKind;
  // Bomb-arc state — only used for rocket_barrage (Boush bomb drop physics).
  private bombGroundX = 0;
  private bombGroundY = 0;
  private bombInitDist = 1;
  private bombTraveled = 0;
  private bombShadow?: Phaser.GameObjects.Ellipse;
  private bombTumble = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    x: number,
    y: number,
    private readonly target: Enemy,
    private readonly tower: TowerDef,
    private readonly enemies: Enemy[],
    private readonly juice?: JuiceSystem,
    private readonly archetype: Archetype = "stafmage",
  ) {
    this.visual = scene.add.container(x, y);
    this.visualKind = pickVisualKind(tower.id);
    if (this.visualKind === "arrow") this.speed = 760;
    else if (this.visualKind === "flame") this.speed = 620;
    else if (this.visualKind === "tidal_wave") this.speed = 540;
    else if (this.visualKind === "boulder_rocket") this.speed = 580;
    else if (this.visualKind === "lightning") this.speed = 0;
    else if (this.visualKind === "laser_beam") this.speed = 0;
    else if (this.visualKind === "dual_breath") this.speed = 600;
    else if (this.visualKind === "storm_hammer") this.speed = 720;
    else if (this.visualKind === "bone_axe") this.speed = 520;
    else if (this.visualKind === "snowflake") this.speed = 720;
    else if (this.visualKind === "vine_whip") this.speed = 640;
    else if (this.visualKind === "brew_jug") this.speed = 560;
    else if (this.visualKind === "soul_wisp") this.speed = 600;
    else if (this.visualKind === "beast_axes") this.speed = 540;
    else if (this.visualKind === "tentacle") this.speed = 580;
    else if (this.visualKind === "death_scythe") this.speed = 540;
    else if (this.visualKind === "phantom_blade") this.speed = 820;
    else if (this.visualKind === "void_blade") this.speed = 760;
    else if (this.visualKind === "moon_tornado") this.speed = 0;       // instant beam
    else if (this.visualKind === "rocket_barrage") this.speed = 220; // slow heavy bomb
    else if (this.visualKind === "firestorm") this.speed = 0;        // instant impact
    else this.speed = archetype === "gunner" ? 720 : archetype === "handcaster" ? 540 : 460;

    if (this.visualKind === "arrow") this.buildArrow();
    else if (this.visualKind === "flame") this.buildFlame();
    else if (this.visualKind === "tidal_wave") this.buildTidalWave();
    else if (this.visualKind === "boulder_rocket") this.buildBoulderRocket();
    else if (this.visualKind === "firestorm") {
      // Underlord Firestorm — INSTANT AoE pillar of fire on target. No travel.
      this.fireFirestorm(x, y);
    }
    else if (this.visualKind === "lightning") {
      // Lightning is INSTANT — render chained jagged beams from origin through
      // up to N nearby enemies, apply damage immediately, then fade. No travel.
      this.fireChainLightning(x, y);
    }
    else if (this.visualKind === "laser_beam") {
      // Steam — instant focused laser beam from caster to target, splash on hit.
      this.fireLaserBeam(x, y);
    }
    else if (this.visualKind === "dual_breath") this.buildDualBreath();
    else if (this.visualKind === "storm_hammer") this.buildStormHammer();
    else if (this.visualKind === "bone_axe") this.buildBoneAxe();
    else if (this.visualKind === "snowflake") this.buildSnowflake();
    else if (this.visualKind === "vine_whip") this.buildVineWhip();
    else if (this.visualKind === "brew_jug") this.buildBrewJug();
    else if (this.visualKind === "soul_wisp") this.buildSoulWisp();
    else if (this.visualKind === "beast_axes") this.buildBeastAxes();
    else if (this.visualKind === "tentacle") this.buildTentacle();
    else if (this.visualKind === "death_scythe") this.buildDeathScythe();
    else if (this.visualKind === "phantom_blade") this.buildPhantomBlade();
    else if (this.visualKind === "void_blade") this.buildVoidBlade();
    else if (this.visualKind === "moon_tornado") {
      // Moon Tornado — instant golden laser wrapped in spiraling tornado, no travel.
      this.fireMoonTornado(x, y);
    }
    else if (this.visualKind === "rocket_barrage") {
      this.buildRocketBarrage();
      // Initialize bomb-drop physics: track ground position separately so
      // the visual can arc above it while hit-detection runs on the ground line.
      this.bombGroundX = x;
      this.bombGroundY = y;
      this.bombInitDist = Math.max(1, Math.hypot(target.position.x - x, target.position.y - y));
      this.bombTraveled = 0;
      // Ground shadow under landing point — grows + darkens as bomb falls.
      this.bombShadow = scene.add.ellipse(x, y + 1, 6, 2.4, 0x000000, 0.35);
      this.bombShadow.setDepth(this.visual.depth - 1);
    }
    else if (archetype === "handcaster") this.buildEnergyBolt();
    else if (archetype === "gunner") this.buildBullet();
    else this.buildOrb();

    // ---- UNIVERSAL KI GLOW: wrap every traveling projectile in a pulsing
    // element-colored energy halo so shots read as charged anime energy, not
    // tiny dots. Sits BEHIND the hand-built sprite (addAt index 0).
    if (this.speed > 0) {
      const glowOuter = scene.add.circle(0, 0, 9, tower.color, 0.22);
      const glowInner = scene.add.circle(0, 0, 5, 0xffffff, 0.32);
      this.visual.addAt(glowInner, 0);
      this.visual.addAt(glowOuter, 0);
      scene.tweens.add({
        targets: glowOuter,
        scale: { from: 0.8, to: 1.35 },
        alpha: { from: 0.22, to: 0.08 },
        yoyo: true,
        repeat: -1,
        duration: 200,
        ease: "Sine.easeInOut",
      });
      scene.tweens.add({
        targets: glowInner,
        scale: { from: 0.7, to: 1.1 },
        yoyo: true,
        repeat: -1,
        duration: 160,
        ease: "Sine.easeInOut",
      });
    }
  }

  /** Draws one forked jagged bolt segment between two world points onto graphics g. */
  private drawBoltSegment(g: Phaser.GameObjects.Graphics, sx: number, sy: number, ex: number, ey: number): void {
    const dx = ex - sx;
    const dy = ey - sy;
    const dist = Math.hypot(dx, dy);
    const px = dist === 0 ? 0 : -dy / dist;
    const py = dist === 0 ? 0 : dx / dist;
    const segments = Math.max(6, Math.floor(dist / 14));
    const points: { x: number; y: number }[] = [{ x: sx, y: sy }];
    for (let i = 1; i < segments; i++) {
      const t = i / segments;
      const fwd = t + (Math.random() - 0.5) * 0.04;
      // Tighter jitter — Zeus bolts read as a thin, taut thread of lightning.
      const offset = (Math.random() - 0.5) * 6;
      points.push({ x: sx + dx * fwd + px * offset, y: sy + dy * fwd + py * offset });
    }
    points.push({ x: ex, y: ey });

    // Thin layered bolt: faint blue haze, slim gold mid, hairline white core.
    g.lineStyle(2.6, 0x88ccff, 0.32);
    g.beginPath();
    g.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) g.lineTo(points[i].x, points[i].y);
    g.strokePath();
    g.lineStyle(1.3, 0xffd84a, 0.9);
    g.beginPath();
    g.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) g.lineTo(points[i].x, points[i].y);
    g.strokePath();
    g.lineStyle(0.7, 0xffffff, 1);
    g.beginPath();
    g.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) g.lineTo(points[i].x, points[i].y);
    g.strokePath();

    // A single short hairline fork — keep the bolt clean, not bushy.
    const forkCount = Math.random() < 0.6 ? 1 : 0;
    for (let f = 0; f < forkCount; f++) {
      const anchorIdx = Math.floor(points.length * (0.3 + Math.random() * 0.5));
      const ax = points[anchorIdx].x;
      const ay = points[anchorIdx].y;
      const angle = Math.atan2(dy, dx) + (Math.random() < 0.5 ? -1 : 1) * (0.5 + Math.random() * 0.6);
      const forkLen = 8 + Math.random() * 10;
      const forkSegs = 2 + Math.floor(Math.random() * 2);
      const fpts: { x: number; y: number }[] = [{ x: ax, y: ay }];
      let cx = ax, cy = ay;
      for (let i = 1; i <= forkSegs; i++) {
        const stepLen = forkLen / forkSegs;
        const jitter = (Math.random() - 0.5) * 6;
        cx += Math.cos(angle) * stepLen + jitter * 0.3;
        cy += Math.sin(angle) * stepLen + jitter * 0.3;
        fpts.push({ x: cx, y: cy });
      }
      g.lineStyle(1, 0xc8e8ff, 0.7);
      g.beginPath();
      g.moveTo(fpts[0].x, fpts[0].y);
      for (let i = 1; i < fpts.length; i++) g.lineTo(fpts[i].x, fpts[i].y);
      g.strokePath();
      g.lineStyle(0.5, 0xffffff, 0.95);
      g.beginPath();
      g.moveTo(fpts[0].x, fpts[0].y);
      for (let i = 1; i < fpts.length; i++) g.lineTo(fpts[i].x, fpts[i].y);
      g.strokePath();
    }
  }

  /**
   * Underlord Firestorm — instant AoE pillar of fire descends on the primary
   * target. All enemies within the tower's splash radius take full damage.
   * Visual: bright fire column from sky + expanding lava ring + floating embers.
   */
  /**
   * Centralized damage application — rolls crit, draws the floating number,
   * deals damage, and applies the tower's DOT + chill. Every projectile hit
   * (primary or splash) routes through here so crit/slow stay consistent.
   */
  private hit(e: Enemy, baseDmg: number, x: number, y: number, color: number, now: number): void {
    if (!e.alive) return;
    let dmg = Math.max(1, Math.round(baseDmg));
    let isCrit = false;
    if (this.tower.crit && Math.random() < this.tower.crit.chance) {
      dmg = Math.round(dmg * this.tower.crit.mult);
      isCrit = true;
    }
    this.juice?.onHit(x, y, dmg, color, isCrit);
    e.takeDamage(dmg);
    if (this.tower.dot) e.applyDot(this.tower.dot.dps, this.tower.dot.duration, now);
    if (this.tower.slow) e.applySlow(this.tower.slow.factor, this.tower.slow.duration, now);
  }

  /**
   * Draw an asphalt/cobble road-fracture pattern centred at (tx,ty) with
   * radius proportional to `splash`. Persists `persistMs` then fades 320ms.
   * Palette is warm browns + near-black gap (NO white/glass tones — this is
   * cracked road revealing dirt sub-base, not shattered glass). Used by
   * Underlord Firestorm AND Tinker bomb so both heavy AOE strikes leave
   * matching ground craters.
   */
  private drawRoadShatter(tx: number, ty: number, splash: number, persistMs = 1700): void {
    const sc = this.scene;
    const shatterR = splash * 1.05;

    // Clip to road tiles only — never paint cracks on grass. Build a geometry
    // mask from every path/spawn/end cell rectangle. Mask must be on the
    // display list (Phaser 4 stencil-buffer requirement) but invisible.
    const grid = (sc as unknown as { grid?: { cells: { x: number; y: number; type: string }[][]; cellSize: number } }).grid;
    const buildMask = (): Phaser.Display.Masks.GeometryMask | undefined => {
      if (!grid?.cells) return undefined;
      const mg = sc.add.graphics();
      mg.setDepth(-1000);
      mg.fillStyle(0xffffff, 1);
      const half = grid.cellSize / 2;
      for (const row of grid.cells) {
        for (const cell of row) {
          if (cell && (cell.type === "path" || cell.type === "spawn" || cell.type === "end")) {
            mg.fillRect(cell.x - half, cell.y - half, grid.cellSize, grid.cellSize);
          }
        }
      }
      const owned: Phaser.GameObjects.Graphics[] = (this as unknown as { _shatterMasks?: Phaser.GameObjects.Graphics[] })._shatterMasks ?? [];
      owned.push(mg);
      (this as unknown as { _shatterMasks?: Phaser.GameObjects.Graphics[] })._shatterMasks = owned;
      return mg.createGeometryMask();
    };
    const allMasks: Phaser.GameObjects.Graphics[] = [];
    const addMaskedG = (depth?: number): Phaser.GameObjects.Graphics => {
      const g = sc.add.graphics();
      if (depth !== undefined) g.setDepth(depth);
      if (grid?.cells) {
        const mg = sc.add.graphics();
        mg.setDepth(-1000);
        mg.fillStyle(0xffffff, 1);
        const half = grid.cellSize / 2;
        for (const row of grid.cells) {
          for (const cell of row) {
            if (cell && (cell.type === "path" || cell.type === "spawn" || cell.type === "end")) {
              mg.fillRect(cell.x - half, cell.y - half, grid.cellSize, grid.cellSize);
            }
          }
        }
        g.setMask(mg.createGeometryMask());
        allMasks.push(mg);
      }
      return g;
    };
    void buildMask; // legacy hook kept for future re-use; addMaskedG is the path used below.

    // ── Real bomb-on-pavement layered crater ─────────────────────────────
    // Palette: charred near-black bowl, scorched brown gradient, raised
    // ejecta rim, hot orange initial flash, warm dust puffs, asphalt chips.
    const SCORCH_DEEP  = 0x0a0604;   // bowl heart (near-black char)
    const SCORCH_MID   = 0x2a1a0e;   // mid scorch
    const SCORCH_SOFT  = 0x4a2d18;   // outer scorch fade
    const EJECTA_RIM   = 0x7a5a36;   // raised lip — lighter than scorch
    const CRACK_GAP    = 0x140d06;
    const CRACK_DIRT   = 0x4a3520;
    const CRACK_RIM    = 0x6a4f30;
    const STONE_CHIP   = 0x5a4838;
    const STONE_SHADOW = 0x2a1f12;
    const HEAT_RED     = 0xff4a14;
    const HEAT_ORANGE  = 0xffa850;
    const HEAT_YELLOW  = 0xffe28a;
    const DUST_LIGHT   = 0x8a7050;
    const DUST_DARK    = 0x44342a;

    // LAYER A — long-lived SCORCH MARK (under everything, slow fade).
    const scorchG = addMaskedG();
    // Outer soft scorch halo (asymmetric — bombs don't make perfect circles).
    for (let r = 4; r >= 0; r--) {
      const rr = shatterR * (0.55 + r * 0.10);
      const alpha = 0.10 + r * 0.04;
      const col = r === 0 ? SCORCH_DEEP : (r <= 2 ? SCORCH_MID : SCORCH_SOFT);
      scorchG.fillStyle(col, alpha);
      // Slight ellipse + jitter polygon for asymmetry
      const nodes = 16;
      scorchG.beginPath();
      for (let i = 0; i < nodes; i++) {
        const a = (i / nodes) * Math.PI * 2;
        const rj = rr * (0.88 + Math.random() * 0.24);
        const px = tx + Math.cos(a) * rj;
        const py = ty + Math.sin(a) * rj * 0.92; // slightly squashed
        if (i === 0) scorchG.moveTo(px, py); else scorchG.lineTo(px, py);
      }
      scorchG.closePath();
      scorchG.fillPath();
    }
    // Charred bowl heart — small dark center.
    scorchG.fillStyle(SCORCH_DEEP, 0.92);
    scorchG.fillCircle(tx, ty, Math.max(3, shatterR * 0.18));
    scorchG.fillStyle(0x000000, 0.55);
    scorchG.fillCircle(tx, ty, Math.max(2, shatterR * 0.10));

    // LAYER B — RAISED EJECTA RIM (highlight ring around the crater).
    const rimG = addMaskedG();
    const rimNodes = 22;
    const rimR = shatterR * 0.62;
    const rimInnerR = shatterR * 0.50;
    const rimOuterPts: { x: number; y: number }[] = [];
    const rimInnerPts: { x: number; y: number }[] = [];
    for (let i = 0; i < rimNodes; i++) {
      const a = (i / rimNodes) * Math.PI * 2;
      const rj = rimR * (0.92 + Math.random() * 0.18);
      const ij = rimInnerR * (0.86 + Math.random() * 0.18);
      rimOuterPts.push({ x: tx + Math.cos(a) * rj, y: ty + Math.sin(a) * rj * 0.94 });
      rimInnerPts.push({ x: tx + Math.cos(a) * ij, y: ty + Math.sin(a) * ij * 0.94 });
    }
    // Rim band drawn as 22 quads (outer ring poly minus inner).
    rimG.fillStyle(EJECTA_RIM, 0.45);
    for (let i = 0; i < rimNodes; i++) {
      const j = (i + 1) % rimNodes;
      rimG.fillTriangle(rimInnerPts[i].x, rimInnerPts[i].y, rimOuterPts[i].x, rimOuterPts[i].y, rimOuterPts[j].x, rimOuterPts[j].y);
      rimG.fillTriangle(rimInnerPts[i].x, rimInnerPts[i].y, rimOuterPts[j].x, rimOuterPts[j].y, rimInnerPts[j].x, rimInnerPts[j].y);
    }
    // Rim stroke for definition.
    rimG.lineStyle(1.2, CRACK_RIM, 0.55);
    rimG.beginPath();
    rimG.moveTo(rimOuterPts[0].x, rimOuterPts[0].y);
    for (let s = 1; s < rimOuterPts.length; s++) rimG.lineTo(rimOuterPts[s].x, rimOuterPts[s].y);
    rimG.closePath();
    rimG.strokePath();

    // LAYER C — CRACKS (radial + concentric, denser & asymmetric).
    const crackG = addMaskedG();
    const primaryCount = 14; // bumped from 10 for denser real-impact pattern
    for (let i = 0; i < primaryCount; i++) {
      const ang = (i / primaryCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.32;
      const len = shatterR * (0.70 + Math.random() * 0.50); // wider asymmetry
      const segs = 5 + Math.floor(Math.random() * 3);
      const pts: { x: number; y: number }[] = [{ x: tx, y: ty }];
      for (let s = 1; s <= segs; s++) {
        const t = s / segs;
        const jitter = (Math.random() - 0.5) * 7;
        pts.push({
          x: tx + Math.cos(ang) * len * t + Math.sin(ang) * jitter,
          y: ty + Math.sin(ang) * len * t - Math.cos(ang) * jitter,
        });
      }
      // Wider rim shadow under crack (gives 3D depth).
      crackG.lineStyle(3.2, CRACK_RIM, 0.45);
      crackG.beginPath(); crackG.moveTo(pts[0].x, pts[0].y);
      for (let s = 1; s < pts.length; s++) crackG.lineTo(pts[s].x, pts[s].y);
      crackG.strokePath();
      crackG.lineStyle(2.0, CRACK_DIRT, 0.95);
      crackG.beginPath(); crackG.moveTo(pts[0].x, pts[0].y);
      for (let s = 1; s < pts.length; s++) crackG.lineTo(pts[s].x, pts[s].y);
      crackG.strokePath();
      crackG.lineStyle(1.0, CRACK_GAP, 1.0);
      crackG.beginPath(); crackG.moveTo(pts[0].x, pts[0].y);
      for (let s = 1; s < pts.length; s++) crackG.lineTo(pts[s].x, pts[s].y);
      crackG.strokePath();

      // 1-2 branches off each crack, slightly more pronounced.
      const branches = 1 + Math.floor(Math.random() * 2);
      for (let b = 0; b < branches; b++) {
        const idx = Math.max(1, Math.floor(pts.length * (0.4 + Math.random() * 0.45)));
        const ax = pts[idx].x, ay = pts[idx].y;
        const ba = ang + (Math.random() < 0.5 ? -1 : 1) * (0.55 + Math.random() * 0.55);
        const blen = 7 + Math.random() * 14;
        const bsegs = 3;
        const bpts: { x: number; y: number }[] = [{ x: ax, y: ay }];
        let cx2 = ax, cy2 = ay;
        for (let s = 1; s <= bsegs; s++) {
          const j = (Math.random() - 0.5) * 4;
          cx2 += (Math.cos(ba) * blen) / bsegs + j * 0.4;
          cy2 += (Math.sin(ba) * blen) / bsegs + j * 0.4;
          bpts.push({ x: cx2, y: cy2 });
        }
        crackG.lineStyle(1.0, CRACK_GAP, 0.9);
        crackG.beginPath(); crackG.moveTo(bpts[0].x, bpts[0].y);
        for (let s = 1; s < bpts.length; s++) crackG.lineTo(bpts[s].x, bpts[s].y);
        crackG.strokePath();
      }
    }

    // Concentric fracture rings — irregular polygon shells (compression waves).
    for (let ring = 0; ring < 3; ring++) {
      const rr = shatterR * (0.30 + ring * 0.22);
      const nodes = 10;
      const ringPts: { x: number; y: number }[] = [];
      for (let i = 0; i < nodes; i++) {
        const a = (i / nodes) * Math.PI * 2;
        const r = rr * (0.85 + Math.random() * 0.30);
        ringPts.push({ x: tx + Math.cos(a) * r, y: ty + Math.sin(a) * r });
      }
      crackG.lineStyle(1.0, CRACK_RIM, 0.42);
      crackG.beginPath();
      crackG.moveTo(ringPts[0].x, ringPts[0].y);
      for (let s = 1; s < ringPts.length; s++) crackG.lineTo(ringPts[s].x, ringPts[s].y);
      crackG.lineTo(ringPts[0].x, ringPts[0].y);
      crackG.strokePath();
      crackG.lineStyle(0.7, CRACK_GAP, 0.82);
      crackG.beginPath();
      crackG.moveTo(ringPts[0].x, ringPts[0].y);
      for (let s = 1; s < ringPts.length; s++) crackG.lineTo(ringPts[s].x, ringPts[s].y);
      crackG.lineTo(ringPts[0].x, ringPts[0].y);
      crackG.strokePath();
    }

    // LAYER D — DISPLACED SLAB PIECES tilted up at the rim (3-4 of them).
    const slabCount = 3 + Math.floor(Math.random() * 2);
    for (let i = 0; i < slabCount; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = shatterR * (0.42 + Math.random() * 0.18);
      const cx = tx + Math.cos(a) * r;
      const cy = ty + Math.sin(a) * r;
      const w = 5 + Math.random() * 5;
      const h = 3 + Math.random() * 2.5;
      const rot = a + (Math.random() - 0.5) * 0.6;
      const co = Math.cos(rot), si = Math.sin(rot);
      const corners = [
        { x: -w/2, y: -h/2 }, { x: w/2, y: -h/2 },
        { x: w/2, y: h/2 },   { x: -w/2, y: h/2 },
      ].map(p => ({ x: cx + p.x * co - p.y * si, y: cy + p.x * si + p.y * co }));
      // Shadow under slab.
      crackG.fillStyle(STONE_SHADOW, 0.85);
      crackG.fillTriangle(corners[0].x, corners[0].y + 1.4, corners[1].x, corners[1].y + 1.4, corners[2].x, corners[2].y + 1.4);
      crackG.fillTriangle(corners[0].x, corners[0].y + 1.4, corners[2].x, corners[2].y + 1.4, corners[3].x, corners[3].y + 1.4);
      // Slab face.
      crackG.fillStyle(STONE_CHIP, 0.98);
      crackG.fillTriangle(corners[0].x, corners[0].y, corners[1].x, corners[1].y, corners[2].x, corners[2].y);
      crackG.fillTriangle(corners[0].x, corners[0].y, corners[2].x, corners[2].y, corners[3].x, corners[3].y);
      // Top edge highlight.
      crackG.lineStyle(0.8, CRACK_RIM, 0.7);
      crackG.beginPath();
      crackG.moveTo(corners[0].x, corners[0].y); crackG.lineTo(corners[1].x, corners[1].y);
      crackG.strokePath();
    }

    // LAYER E — CENTER cobble chips (small displaced pieces).
    const chipCount = 8;
    for (let i = 0; i < chipCount; i++) {
      const a = (i / chipCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      const r = 3 + Math.random() * 5;
      const cx = tx + Math.cos(a) * r;
      const cy = ty + Math.sin(a) * r;
      const sz = 1.4 + Math.random() * 1.6;
      crackG.fillStyle(STONE_CHIP, 0.95);
      crackG.fillTriangle(
        cx, cy - sz,
        cx + sz, cy + sz * 0.6,
        cx - sz * 0.7, cy + sz,
      );
    }

    // LAYER F — EJECTA DEBRIS thrown OUTSIDE the rim (small dark chunks).
    const ejectaCount = 14;
    for (let i = 0; i < ejectaCount; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = shatterR * (0.70 + Math.random() * 0.40);
      const cx = tx + Math.cos(a) * r;
      const cy = ty + Math.sin(a) * r;
      const sz = 0.9 + Math.random() * 1.8;
      crackG.fillStyle(STONE_SHADOW, 0.85);
      crackG.fillCircle(cx, cy, sz);
    }

    // ── Transient layers (heat flash + dust puffs) ───────────────────────
    // LAYER G — HEAT-FLASH (very short, masked to road).
    const flashG = addMaskedG();
    flashG.fillStyle(HEAT_YELLOW, 0.85);
    flashG.fillCircle(tx, ty, shatterR * 0.28);
    flashG.fillStyle(HEAT_ORANGE, 0.70);
    flashG.fillCircle(tx, ty, shatterR * 0.45);
    flashG.fillStyle(HEAT_RED, 0.50);
    flashG.fillCircle(tx, ty, shatterR * 0.62);
    sc.tweens.add({
      targets: flashG,
      alpha: 0,
      duration: 280,
      ease: "Quad.easeIn",
      onComplete: () => { flashG.clearMask?.(true); flashG.destroy(); },
    });

    // LAYER H — DUST PUFFS rising from the crater (NOT masked, can drift over
    // the rim slightly; but kept centered on the road so visually it stays
    // over the lane).
    const dustGs: Phaser.GameObjects.Graphics[] = [];
    const puffCount = 5;
    for (let i = 0; i < puffCount; i++) {
      const dg = sc.add.graphics();
      const ang = -Math.PI / 2 + (Math.random() - 0.5) * 1.2; // mostly upward
      const dist = shatterR * (0.2 + Math.random() * 0.45);
      const startX = tx + (Math.random() - 0.5) * shatterR * 0.5;
      const startY = ty + (Math.random() - 0.5) * shatterR * 0.4;
      const endX = startX + Math.cos(ang) * dist;
      const endY = startY + Math.sin(ang) * dist;
      const sz0 = 3 + Math.random() * 3;
      const sz1 = sz0 * (2.4 + Math.random());
      const c = (Math.random() < 0.5 ? DUST_LIGHT : DUST_DARK);
      dg.fillStyle(c, 0.55);
      dg.fillCircle(0, 0, 1);
      dg.setPosition(startX, startY);
      dg.setScale(sz0);
      dustGs.push(dg);
      sc.tweens.add({
        targets: dg,
        x: endX,
        y: endY,
        scale: sz1,
        alpha: 0,
        duration: 700 + Math.random() * 350,
        ease: "Quad.easeOut",
        onComplete: () => dg.destroy(),
      });
    }

    // ── Fade lifecycle ──────────────────────────────────────────────────
    // Cracks/rim/chips/ejecta fade in sync with persistMs (the old behavior).
    sc.tweens.add({
      targets: [crackG, rimG],
      alpha: 0,
      duration: 320,
      delay: persistMs,
      ease: "Quad.easeIn",
      onComplete: () => {
        crackG.clearMask?.(true); crackG.destroy();
        rimG.clearMask?.(true); rimG.destroy();
      },
    });
    // Scorch mark fades alongside the cracks now (user feedback: "boush bom
    // black effect on floor stay too long"). Faster fade than before, no
    // extended linger.
    sc.tweens.add({
      targets: scorchG,
      alpha: 0,
      duration: 320,
      delay: persistMs,
      ease: "Quad.easeIn",
      onComplete: () => { scorchG.clearMask?.(true); scorchG.destroy(); },
    });
    // Cleanup all geometry-mask sources.
    sc.time.delayedCall(persistMs + 340, () => {
      for (const m of allMasks) m.destroy();
    });
  }

  private fireFirestorm(_sx: number, _sy: number): void {
    const now = this.scene.time.now;
    const tx = this.target.position.x;
    const ty = this.target.position.y;
    const splash = this.tower.splash ?? 32;

    const LAVA      = 0xff4400;
    const LAVA_HOT  = 0xff8800;
    const LAVA_WH   = 0xffdd66;
    const LAVA_DARK = 0xcc2200;

    // ── Damage: full to all enemies within splash radius ──────────────
    for (const e of this.enemies) {
      if (!e.alive) continue;
      const d = Math.hypot(e.position.x - tx, e.position.y - ty);
      if (d <= splash) {
        const dmg = Math.max(1, Math.round(this.tower.damage * (1 - d / (splash * 1.6 + 1))));
        this.hit(e, dmg, e.position.x, e.position.y - 2, this.tower.color, now);
      }
    }

    const sc = this.scene;

    // ── Phase 0 (t=0): SKY-STREAK METEOR descending into the strike point ──
    // Sells "pillar of fire from the sky" — a bright comet plummets from
    // 200px above, leaving a fading fiery tail, lands at the impact point
    // ~110ms before the pillar bursts.
    const STREAK_LEN = 220;
    const sx0 = tx + (Math.random() - 0.5) * 24;
    const sy0 = ty - STREAK_LEN;
    const cometOuter = sc.add.circle(sx0, sy0, 12, LAVA_HOT, 0.55);
    const cometCore  = sc.add.circle(sx0, sy0, 6,  0xfff080, 1);
    // Trail dots dropped along the descent path.
    const trailDots: Phaser.GameObjects.Arc[] = [];
    const trailTimer = sc.time.addEvent({
      delay: 18,
      repeat: 6,
      callback: () => {
        const td = sc.add.circle(cometCore.x, cometCore.y, 4 + Math.random() * 2, LAVA, 0.7);
        trailDots.push(td);
        sc.tweens.add({
          targets: td,
          alpha: 0,
          scale: 0.4,
          duration: 240,
          ease: "Quad.easeOut",
          onComplete: () => td.destroy(),
        });
      },
    });
    sc.tweens.add({
      targets: [cometCore, cometOuter],
      x: tx,
      y: ty,
      duration: 110,
      ease: "Quad.easeIn", // accelerate as it falls
      onComplete: () => {
        cometCore.destroy();
        cometOuter.destroy();
        trailTimer.remove();
      },
    });

    // ── Phase 1: pre-impact red floor warning ring REMOVED per user request ──
    // (User: "remove vrogros skill red effect on floor"). The comet streak
    // overhead + camera shake telegraph the strike zone without painting red
    // on the ground.

    // ── Camera shake on impact (t=130ms) for weight ────────────────────
    sc.time.delayedCall(130, () => {
      sc.cameras.main.shake(110, 0.0022);
    });

    // ── Phase 2 (t=120ms): single BRIGHT YELLOW fire pillar ─────────────
    // One tall central pillar (was 3 staggered — flankers removed for clarity).
    // Yellow dominates: outer red-orange aura → orange body → BRIGHT YELLOW
    // core → white-hot inner filament. Wider + taller for high visibility.
    const YELLOW       = 0xffd84a;
    const YELLOW_BRIGHT= 0xfff080;
    const PILLAR_CONFIGS = [
      // Slimmed: roughly 55% of previous widths so it reads as a tall focused
      // column rather than a chubby block. Height unchanged.
      { delay: 120, x: tx, w: 26, mw: 16, cw: 9, fw: 4, height: 160, alpha: 1.0 },
    ];
    for (const cfg of PILLAR_CONFIGS) {
      sc.time.delayedCall(cfg.delay, () => {
        const h = cfg.height;
        // Outer red-orange glow (LAVA).
        const pGlow = sc.add.rectangle(cfg.x, ty - h / 2, cfg.w,  h, LAVA,        cfg.alpha * 0.50);
        // Mid orange body (LAVA_HOT).
        const pMid  = sc.add.rectangle(cfg.x, ty - h / 2, cfg.mw, h, LAVA_HOT,    cfg.alpha * 0.85);
        // Bright yellow core — the dominant visible flame body.
        const pCore = sc.add.rectangle(cfg.x, ty - h / 2, cfg.cw, h, YELLOW,      cfg.alpha);
        // White-hot inner filament.
        const pFil  = sc.add.rectangle(cfg.x, ty - h / 2, cfg.fw, h, YELLOW_BRIGHT, cfg.alpha);

        // Premium realism: tapered FLAME LICKS at the top — 5 jagged tongues
        // pointing skyward, randomized width/height/lean. Drawn once via
        // graphics (cheaper than 5 separate gameobjects) and pulsing alpha
        // for "flickering" feel.
        const lickTopG = sc.add.graphics();
        const tipBaseY = ty - h;          // top of pillar
        const lickCount = 5;
        for (let l = 0; l < lickCount; l++) {
          const offX = (l - (lickCount - 1) / 2) * (cfg.w / 5) + (Math.random() - 0.5) * 3;
          const baseW = (cfg.cw / 1.6) * (0.7 + Math.random() * 0.4);
          const lickH = 14 + Math.random() * 18;
          const tipLean = (Math.random() - 0.5) * 6;
          // Outer orange lick.
          lickTopG.fillStyle(LAVA_HOT, cfg.alpha * 0.85);
          lickTopG.fillTriangle(
            cfg.x + offX - baseW / 2, tipBaseY,
            cfg.x + offX + baseW / 2, tipBaseY,
            cfg.x + offX + tipLean,   tipBaseY - lickH,
          );
          // Inner yellow lick.
          lickTopG.fillStyle(YELLOW, cfg.alpha);
          lickTopG.fillTriangle(
            cfg.x + offX - baseW / 3, tipBaseY,
            cfg.x + offX + baseW / 3, tipBaseY,
            cfg.x + offX + tipLean * 0.7, tipBaseY - lickH * 0.85,
          );
          // White-hot core lick.
          lickTopG.fillStyle(YELLOW_BRIGHT, cfg.alpha);
          lickTopG.fillTriangle(
            cfg.x + offX - baseW / 6, tipBaseY,
            cfg.x + offX + baseW / 6, tipBaseY,
            cfg.x + offX + tipLean * 0.4, tipBaseY - lickH * 0.7,
          );
        }

        // Premium realism: BASE flame spread — wider tongues hugging the
        // ground at the foot of the pillar (sells the impact's footprint).
        const lickBaseG = sc.add.graphics();
        const baseY0 = ty;
        const baseLickCount = 6;
        for (let l = 0; l < baseLickCount; l++) {
          const ang = -Math.PI + (l / (baseLickCount - 1)) * Math.PI; // along ground
          const reach = (cfg.w * 0.65) * (0.6 + Math.random() * 0.6);
          const cx2 = cfg.x + Math.cos(ang) * reach * 0.5;
          const cy2 = baseY0 + Math.sin(ang) * 1; // mostly horizontal
          const baseW2 = 6 + Math.random() * 4;
          const lickH2 = 8 + Math.random() * 8;
          // Orange spread.
          lickBaseG.fillStyle(LAVA_HOT, cfg.alpha * 0.7);
          lickBaseG.fillTriangle(
            cx2 - baseW2 / 2, cy2,
            cx2 + baseW2 / 2, cy2,
            cx2 + Math.cos(ang) * lickH2 * 0.6, cy2 - lickH2,
          );
          // Yellow inner spread.
          lickBaseG.fillStyle(YELLOW, cfg.alpha * 0.85);
          lickBaseG.fillTriangle(
            cx2 - baseW2 / 3, cy2,
            cx2 + baseW2 / 3, cy2,
            cx2 + Math.cos(ang) * lickH2 * 0.55, cy2 - lickH2 * 0.85,
          );
        }

        // Slam in from top: start displaced up, drop into position, then linger before fading.
        const allParts = [pGlow, pMid, pCore, pFil, lickTopG, lickBaseG];
        allParts.forEach(p => { p.setY(p.y - 30); (p as any).alpha = 0; });
        sc.tweens.add({
          targets: allParts,
          y: `+=${30}`,
          alpha: cfg.alpha,
          duration: 80,
          ease: "Quad.easeIn",
          onComplete: () => {
            // Mid-life flicker on the licks for "live fire" feel.
            sc.tweens.add({
              targets: [lickTopG, lickBaseG],
              alpha: cfg.alpha * 0.55,
              duration: 90,
              yoyo: true,
              repeat: 2,
              ease: "Sine.inOut",
            });
            // Linger longer, then fade up — keeps the yellow visible.
            sc.tweens.add({
              targets: allParts,
              alpha: 0,
              y: `-=${28}`,
              duration: 520,
              delay: 120,
              ease: "Quad.easeIn",
              onComplete: () => allParts.forEach(p => p.destroy()),
            });
          },
        });
      });
    }

    // ── Phase 3 (t=130ms): Impact burst ───────────────────────────────
    sc.time.delayedCall(130, () => {
      // Yellow palette (matches firestorm pillar) — was cyan/blue, swapped
      // per request so the encircling arcs read as fire-electricity, not ice.
      const ELEC_CYAN = 0xfff080;   // bright yellow core (was 0x9be8ff cyan)
      const ELEC_BLUE = 0xffa030;   // gold-orange glow (was 0x4cc9f0 blue)

      // ── ROAD-SHATTER (asphalt/cobble) — shared helper, brown palette ──
      this.drawRoadShatter(tx, ty, splash, 1700);

      // ── MOLTEN LAVA PUDDLES REMOVED per user request ────────────────
      // (User: "remove vrogros skill red effect on floor"). The puddles
      // settled to dark-red lava on the road and read as red floor scorch.

      // ── DARK SMOKE PLUME rising from impact (5 puffs over ~1.2s) ─────
      // Reads as "ground was just slammed and is venting heat/smoke".
      // Charcoal grey, drifts upward + outward, scales up + fades.
      for (let i = 0; i < 5; i++) {
        const sxx = tx + (Math.random() - 0.5) * splash * 0.45;
        const syy = ty + (Math.random() - 0.5) * 8;
        const puff = sc.add.circle(sxx, syy, 7 + Math.random() * 4, 0x2a2018, 0.65);
        sc.tweens.add({
          targets: puff,
          x: sxx + (Math.random() - 0.5) * 22,
          y: syy - 22 - Math.random() * 22,
          scale: 2.4 + Math.random() * 0.6,
          alpha: 0,
          duration: 1100 + Math.random() * 360,
          delay: 80 + i * 40,
          ease: "Quad.easeOut",
          onComplete: () => puff.destroy(),
        });
      }

      // ── FALLING EMBERS (gravity-pulled, descend with the pillar) ─────
      // 8 embers spawn slightly above impact and fall vertically with slight
      // drift — looks like cinders raining out of the firestorm column.
      for (let i = 0; i < 8; i++) {
        const ex0 = tx + (Math.random() - 0.5) * splash * 0.85;
        const ey0 = ty - 60 - Math.random() * 30;
        const sz = 1.2 + Math.random() * 1.8;
        const col = [LAVA_WH, LAVA_HOT, LAVA][Math.floor(Math.random() * 3)];
        const ember = sc.add.circle(ex0, ey0, sz, col, 1);
        sc.tweens.add({
          targets: ember,
          x: ex0 + (Math.random() - 0.5) * 18,
          y: ty + 4 + Math.random() * 6,
          alpha: 0,
          scale: 0.4,
          duration: 380 + Math.random() * 180,
          delay: Math.random() * 140,
          ease: "Quad.easeIn",  // gravity feel — accelerate downward
          onComplete: () => ember.destroy(),
        });
      }

      // ── Electric pulse rings (2 staggered cyan rings) ─────────────
      const pulseRing1 = sc.add.circle(tx, ty, 6, 0, 0).setStrokeStyle(2.4, ELEC_CYAN, 1);
      sc.tweens.add({
        targets: pulseRing1,
        scale: splash / 3.0,
        alpha: 0,
        duration: 360,
        ease: "Cubic.easeOut",
        onComplete: () => pulseRing1.destroy(),
      });
      sc.time.delayedCall(80, () => {
        const pulseRing2 = sc.add.circle(tx, ty, 6, 0, 0).setStrokeStyle(1.6, 0xffffff, 0.95);
        sc.tweens.add({
          targets: pulseRing2,
          scale: splash / 3.6,
          alpha: 0,
          duration: 320,
          ease: "Cubic.easeOut",
          onComplete: () => pulseRing2.destroy(),
        });
      });

      // ── ENCIRCLING electrical waves: vertical lightning columns AROUND ─
      // the pillar (8 arcs in a ring around the strike, climbing up the
      // pillar height). Repeats 2-3 times in a flicker pattern.
      const ringR = splash * 0.95;
      const arcCount = 8;
      const pillarH = 130;
      let flickerCount = 0;
      const flickerLightning = () => {
        const elecG = sc.add.graphics();
        for (let i = 0; i < arcCount; i++) {
          const ang = (i / arcCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.18;
          const baseX = tx + Math.cos(ang) * ringR;
          const baseY = ty + Math.sin(ang) * ringR * 0.45; // flatter ellipse for ground perspective
          // Vertical bolt climbing upward from ring point.
          const segs = 6;
          const topY = ty - pillarH * (0.6 + Math.random() * 0.4);
          const pts: { x: number; y: number }[] = [{ x: baseX, y: baseY }];
          for (let s = 1; s <= segs; s++) {
            const t = s / segs;
            const py = baseY + (topY - baseY) * t;
            const px = baseX + (Math.random() - 0.5) * 9;
            pts.push({ x: px, y: py });
          }
          // Outer blue glow.
          elecG.lineStyle(3.2, ELEC_BLUE, 0.6);
          elecG.beginPath(); elecG.moveTo(pts[0].x, pts[0].y);
          for (let s = 1; s < pts.length; s++) elecG.lineTo(pts[s].x, pts[s].y);
          elecG.strokePath();
          // Cyan core.
          elecG.lineStyle(1.6, ELEC_CYAN, 1);
          elecG.beginPath(); elecG.moveTo(pts[0].x, pts[0].y);
          for (let s = 1; s < pts.length; s++) elecG.lineTo(pts[s].x, pts[s].y);
          elecG.strokePath();
          // White-hot filament.
          elecG.lineStyle(0.7, 0xffffff, 1);
          elecG.beginPath(); elecG.moveTo(pts[0].x, pts[0].y);
          for (let s = 1; s < pts.length; s++) elecG.lineTo(pts[s].x, pts[s].y);
          elecG.strokePath();
        }
        sc.tweens.add({
          targets: elecG,
          alpha: 0,
          duration: 140,
          delay: 60,
          ease: "Quad.easeIn",
          onComplete: () => elecG.destroy(),
        });
        // Encircling base ring drawn each flicker — connects bolt feet around the strike.
        const baseRing = sc.add.ellipse(tx, ty, ringR * 2, ringR * 0.9, 0, 0)
          .setStrokeStyle(1.4, ELEC_CYAN, 0.85);
        sc.tweens.add({
          targets: baseRing,
          alpha: 0,
          duration: 200,
          ease: "Quad.easeIn",
          onComplete: () => baseRing.destroy(),
        });
        flickerCount++;
        if (flickerCount < 3) sc.time.delayedCall(160, flickerLightning);
      };
      flickerLightning();

      // Shockwave ring — thin bright ring expands fast.
      const shockRing = sc.add.circle(tx, ty, 4, 0xffffff, 1).setStrokeStyle(3, LAVA_WH, 1);
      sc.tweens.add({
        targets: shockRing,
        scale: splash / 3.5,
        alpha: 0,
        duration: 280,
        ease: "Cubic.easeOut",
        onComplete: () => shockRing.destroy(),
      });

      // White-hot core flash.
      const flash = sc.add.circle(tx, ty, 10, 0xffffff, 1);
      sc.tweens.add({
        targets: flash,
        scale: 3.2,
        alpha: 0,
        duration: 200,
        ease: "Quad.easeOut",
        onComplete: () => flash.destroy(),
      });

      // Lava fill circle — expands to splash radius, slower fade.
      const lavaFill = sc.add.circle(tx, ty, 6, LAVA, 0.6);
      sc.tweens.add({
        targets: lavaFill,
        scale: splash / 5.5,
        alpha: 0,
        duration: 500,
        ease: "Cubic.easeOut",
        onComplete: () => lavaFill.destroy(),
      });

      // ── Ground crack lines (8 radiating fissures) ──────────────────
      const g = sc.add.graphics();
      const crackCount = 8;
      for (let i = 0; i < crackCount; i++) {
        const angle = (i / crackCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
        const len = splash * (0.7 + Math.random() * 0.5);
        // Build jagged crack: 4-5 segments with slight angular jitter.
        const segs = 4 + Math.floor(Math.random() * 2);
        let cx2 = tx, cy2 = ty;
        const pts: { x: number; y: number }[] = [{ x: cx2, y: cy2 }];
        for (let s = 1; s <= segs; s++) {
          const t = s / segs;
          const jitter = (Math.random() - 0.5) * 6;
          cx2 = tx + Math.cos(angle) * len * t + Math.sin(angle) * jitter;
          cy2 = ty + Math.sin(angle) * len * t - Math.cos(angle) * jitter;
          pts.push({ x: cx2, y: cy2 });
        }
        // Outer glow crack.
        g.lineStyle(3.5, LAVA_DARK, 0.7);
        g.beginPath(); g.moveTo(pts[0].x, pts[0].y);
        for (let s = 1; s < pts.length; s++) g.lineTo(pts[s].x, pts[s].y);
        g.strokePath();
        // Inner bright crack.
        g.lineStyle(1.4, LAVA_WH, 1);
        g.beginPath(); g.moveTo(pts[0].x, pts[0].y);
        for (let s = 1; s < pts.length; s++) g.lineTo(pts[s].x, pts[s].y);
        g.strokePath();
      }
      sc.tweens.add({
        targets: g,
        alpha: 0,
        duration: 600,
        delay: 100,
        ease: "Quad.easeIn",
        onComplete: () => g.destroy(),
      });

      // ── Radial ember spray (12 sparks, all directions) ─────────────
      for (let i = 0; i < 12; i++) {
        const angle = (i / 12) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
        const dist = splash * (0.5 + Math.random() * 0.7);
        const ex = tx + Math.cos(angle) * dist;
        const ey = ty + Math.sin(angle) * dist;
        const size = 1.6 + Math.random() * 2.4;
        const col = [LAVA, LAVA_HOT, LAVA_WH][Math.floor(Math.random() * 3)];
        const spark = sc.add.circle(tx, ty, size, col, 1);
        sc.tweens.add({
          targets: spark,
          x: ex,
          y: ey - 8 - Math.random() * 16,
          alpha: 0,
          scale: 0.3,
          duration: 380 + Math.random() * 200,
          ease: "Quad.easeOut",
          onComplete: () => spark.destroy(),
        });
      }

      // ── Lingering heat shimmer columns (rising wisps) ──────────────
      for (let i = 0; i < 5; i++) {
        const wx = tx + (Math.random() - 0.5) * splash * 1.2;
        const wy = ty;
        const wisp = sc.add.rectangle(wx, wy, 1.5, 18, LAVA_HOT, 0.55);
        sc.tweens.add({
          targets: wisp,
          y: wy - 28 - Math.random() * 18,
          alpha: 0,
          scaleX: 0.3,
          duration: 500 + Math.random() * 200,
          delay: 80 + Math.random() * 120,
          ease: "Sine.easeOut",
          onComplete: () => wisp.destroy(),
        });
      }
    });

    this.visual.destroy();
    this.alive = false;
  }

  /**
   * Chain lightning — draws jagged forked beams from (sx,sy) through up to
   * MAX_CHAIN_TARGETS nearby enemies, hopping target → next-nearest within
   * chainRange. Damage applied to each (full damage on primary, falloff per hop).
   */
  private fireChainLightning(sx: number, sy: number): void {
    const MAX_CHAIN_TARGETS = 4;          // total enemies hit (incl. primary)
    const CHAIN_RANGE = 130;              // max jump distance px
    const FALLOFF = 0.75;                 // damage multiplier per additional hop

    const now = this.scene.time.now;
    const g = this.scene.add.graphics();

    // Build chain: primary first, then nearest unhit alive enemy within CHAIN_RANGE.
    const chain: Enemy[] = [this.target];
    const visited = new Set<Enemy>([this.target]);
    let prev = this.target;
    while (chain.length < MAX_CHAIN_TARGETS) {
      let best: Enemy | null = null;
      let bestDist = CHAIN_RANGE;
      for (const e of this.enemies) {
        if (!e.alive || visited.has(e)) continue;
        const ep = e.position;
        const d = Math.hypot(ep.x - prev.position.x, ep.y - prev.position.y);
        if (d <= bestDist) { bestDist = d; best = e; }
      }
      if (!best) break;
      chain.push(best);
      visited.add(best);
      prev = best;
    }

    // Draw bolt from caster to first target, then arc target → target.
    let lx = sx, ly = sy;
    for (let i = 0; i < chain.length; i++) {
      const e = chain[i];
      this.drawBoltSegment(g, lx, ly, e.position.x, e.position.y);
      lx = e.position.x;
      ly = e.position.y;
    }

    // Apply damage to each chained enemy with falloff.
    for (let i = 0; i < chain.length; i++) {
      const e = chain[i];
      const mult = Math.pow(FALLOFF, i);
      const dmg = Math.max(1, Math.round(this.tower.damage * mult));
      this.hit(e, dmg, e.position.x, e.position.y - 2, this.tower.color, now);

      // Quick flash impact at each strike point.
      const pop = this.scene.add.circle(e.position.x, e.position.y, 10, 0xffffff, 1);
      this.scene.tweens.add({
        targets: pop,
        scale: 3,
        alpha: 0,
        duration: 200,
        onComplete: () => pop.destroy(),
      });
      // Electric ring at each chained enemy.
      const eRing = this.scene.add.circle(e.position.x, e.position.y, 8, 0x88ccff, 0.7).setStrokeStyle(2, 0xffffff, 0.9);
      this.scene.tweens.add({
        targets: eRing,
        scale: 2.6,
        alpha: 0,
        duration: 260,
        ease: "Cubic.easeOut",
        onComplete: () => eRing.destroy(),
      });
    }

    // Beam fade-out: brief flicker then dissolve.
    this.scene.tweens.add({
      targets: g,
      alpha: 0,
      duration: 200,
      ease: "Quad.easeIn",
      onComplete: () => g.destroy(),
    });

    // The empty container at (sx,sy) isn't needed for lightning — clean it up.
    this.visual.destroy();
    this.alive = false;
  }

  /** Steam laser — instant focused beam from caster to target, splashes on hit. */
  private fireLaserBeam(sx: number, sy: number): void {
    const now = this.scene.time.now;
    const tx = this.target.position.x;
    const ty = this.target.position.y;
    const color = this.tower.color;          // steam = pale ice-blue (0xb6e6ff)

    // Outer wide glow halo along the beam line.
    const glowG = this.scene.add.graphics();
    glowG.lineStyle(7, color, 0.35);
    glowG.lineBetween(sx, sy, tx, ty);
    // Mid beam — softer color band.
    glowG.lineStyle(3.5, 0xeaf6ff, 0.85);
    glowG.lineBetween(sx, sy, tx, ty);
    // Inner white-hot core line.
    glowG.lineStyle(1.4, 0xffffff, 1);
    glowG.lineBetween(sx, sy, tx, ty);

    // Muzzle flash at caster origin.
    const muzzle = this.scene.add.circle(sx, sy, 5, 0xffffff, 1);
    this.scene.tweens.add({
      targets: muzzle,
      scale: 2.2,
      alpha: 0,
      duration: 180,
      ease: "Quad.easeOut",
      onComplete: () => muzzle.destroy(),
    });
    const muzzleHalo = this.scene.add.circle(sx, sy, 8, color, 0.7);
    this.scene.tweens.add({
      targets: muzzleHalo,
      scale: 2.8,
      alpha: 0,
      duration: 240,
      ease: "Quad.easeOut",
      onComplete: () => muzzleHalo.destroy(),
    });

    // Beam fade — quick blink off.
    this.scene.tweens.add({
      targets: glowG,
      alpha: 0,
      duration: 160,
      ease: "Quad.easeIn",
      onComplete: () => glowG.destroy(),
    });

    // Apply primary hit + splash if the tower has it.
    const dmg = Math.round(this.tower.damage);
    this.hit(this.target, dmg, tx, ty - 2, color, now);

    // Splash — pale steam puff hitting nearby enemies in a small radius.
    if (this.tower.splash > 0) {
      for (const e of this.enemies) {
        if (!e.alive || e === this.target) continue;
        const ep = e.position;
        const d = Math.hypot(ep.x - tx, ep.y - ty);
        if (d <= this.tower.splash) {
          const splashDmg = Math.max(1, Math.round(dmg * 0.6));
          this.hit(e, splashDmg, ep.x, ep.y - 2, color, now);
        }
      }
    }

    // Impact burst at target — steam ring + bright pop.
    const pop = this.scene.add.circle(tx, ty, 6, 0xffffff, 1);
    this.scene.tweens.add({
      targets: pop,
      scale: 2.4,
      alpha: 0,
      duration: 220,
      ease: "Quad.easeOut",
      onComplete: () => pop.destroy(),
    });
    const ring = this.scene.add.circle(tx, ty, 10, color, 0).setStrokeStyle(2.4, color, 0.95);
    this.scene.tweens.add({
      targets: ring,
      scale: 2.2,
      alpha: 0,
      duration: 320,
      ease: "Cubic.easeOut",
      onComplete: () => ring.destroy(),
    });
    // Steam puffs drifting up at impact point.
    for (let i = 0; i < 4; i++) {
      const ang = -Math.PI / 2 + (Math.random() - 0.5) * 1.8;
      const puff = this.scene.add.circle(tx, ty, 2 + Math.random() * 1.6, color, 0.85);
      this.scene.tweens.add({
        targets: puff,
        x: tx + Math.cos(ang) * 12,
        y: ty + Math.sin(ang) * 12,
        scale: 1.6,
        alpha: 0,
        duration: 420 + Math.random() * 160,
        ease: "Quad.easeOut",
        onComplete: () => puff.destroy(),
      });
    }

    this.visual.destroy();
    this.alive = false;
  }

  /** Lina Dragon Slave — angled flame wave with swept-back tongues. Forward-pointing, NOT a round orb. */
  private buildFlame(): void {
    // This visual rotates with travel direction (handled in update()) — so local +x = forward.
    // Outer deep-red elongated glow aura — stretched along travel axis.
    const aura = this.scene.add.ellipse(0, 0, 22, 12, 0xff2a00, 0.35);
    // Forward-pointing dragon-head flame body — sharp triangle leading edge.
    const head = this.scene.add.triangle(6, 0, 0, 0, -10, -6, -10, 6, 0xff6a1a)
      .setStrokeStyle(0.8, 0xff2a00, 1);
    // Hot inner yellow core — narrower triangle inside the head.
    const core = this.scene.add.triangle(4, 0, 0, 0, -8, -3.5, -8, 3.5, 0xfff2a8);
    // Three trailing flame tongues swept BACKWARD (negative x = behind in travel dir).
    const tailUp   = this.scene.add.triangle(-9, -3, 0, 0, -7, -4, -7, 1, 0xff5a1a, 0.9);
    const tailMid  = this.scene.add.triangle(-12, 0, 0, 0, -8, -2, -8, 2, 0xffaa1a, 0.95);
    const tailDown = this.scene.add.triangle(-9, 3, 0, 0, -7, -1, -7, 4, 0xff5a1a, 0.85);
    // Bright leading-edge spark dot (tip).
    const tip = this.scene.add.circle(7, 0, 2.2, 0xffffff, 1);
    // Pulsing scale on the whole flame.
    this.scene.tweens.add({
      targets: [aura, head, core],
      scaleX: { from: 0.92, to: 1.08 },
      scaleY: { from: 0.85, to: 1.15 },
      yoyo: true,
      repeat: -1,
      duration: 140,
    });
    // Tail flicker — independent rapid flutter.
    this.scene.tweens.add({
      targets: [tailUp, tailMid, tailDown],
      scaleX: { from: 0.7, to: 1.3 },
      alpha: { from: 0.6, to: 1 },
      yoyo: true,
      repeat: -1,
      duration: 95,
    });
    // Tip flicker.
    this.scene.tweens.add({
      targets: tip,
      scale: { from: 0.7, to: 1.2 },
      alpha: { from: 0.7, to: 1 },
      yoyo: true,
      repeat: -1,
      duration: 110,
    });
    this.visual.add([aura, tailUp, tailMid, tailDown, head, core, tip]);
  }

  /** Jakiro Dual Breath — twin dragon-breath cones: blue-ice top, red-fire bottom, fanning forward. */
  private buildDualBreath(): void {
    // This visual rotates with travel direction — local +x = forward.
    // Outer blended aura (purple-ish where the two breaths overlap).
    const aura = this.scene.add.ellipse(2, 0, 26, 16, 0x9a6cff, 0.28);

    // ----- BLUE ICE BREATH (upper stream) -----
    // Long cone body fanning forward-and-up.
    const iceCone = this.scene.add.triangle(8, -4, -10, 0, 10, -5, 10, 1, 0x4cc9f0, 0.85)
      .setStrokeStyle(0.6, 0xffffff, 0.7);
    // Bright icy core inside the cone.
    const iceCore = this.scene.add.triangle(7, -3, -7, 0, 8, -3.5, 8, 0.5, 0xeaf6ff, 0.95);
    // Frost crystal speck near the cone tip.
    const iceCrystal = this.scene.add.triangle(11, -5, 0, -2, -2, 1, 2, 1, 0xffffff, 1);
    // Trailing icy puff at the back.
    const icePuff = this.scene.add.ellipse(-6, -3, 8, 4, 0xa6e3ff, 0.85);

    // ----- RED FIRE BREATH (lower stream) -----
    // Long cone body fanning forward-and-down.
    const fireCone = this.scene.add.triangle(8, 4, -10, 0, 10, -1, 10, 5, 0xff5a1a, 0.9)
      .setStrokeStyle(0.6, 0xff2a00, 0.8);
    // Hot inner yellow core.
    const fireCore = this.scene.add.triangle(7, 3, -7, 0, 8, -0.5, 8, 3.5, 0xfff2a8, 0.95);
    // Bright ember spark near the cone tip.
    const fireSpark = this.scene.add.circle(11, 5, 1.6, 0xffe070, 1);
    // Trailing flame tongue at the back.
    const firePuff = this.scene.add.ellipse(-6, 3, 8, 4, 0xff7a3a, 0.85);

    // ----- DRAGON SOURCE (back) -----
    // Dark V-shaped maw where both breaths emerge.
    const maw = this.scene.add.triangle(-7, 0, 0, -5, 0, 5, -6, 0, 0x2a1a3a, 0.95)
      .setStrokeStyle(0.6, 0x6a4a8a, 1);
    // Two glowing "dragon eyes" — blue and red — flanking the maw.
    const blueEye = this.scene.add.circle(-9, -4, 1.4, 0x88ccff, 1);
    const redEye = this.scene.add.circle(-9, 4, 1.4, 0xff5a3a, 1);

    // ----- ANIMATIONS -----
    // Both cones pulse/flicker independently for that breath-of-dragon shimmer.
    this.scene.tweens.add({
      targets: [iceCone, iceCore],
      scaleX: { from: 0.85, to: 1.15 },
      scaleY: { from: 0.9, to: 1.1 },
      yoyo: true,
      repeat: -1,
      duration: 130,
    });
    this.scene.tweens.add({
      targets: [fireCone, fireCore],
      scaleX: { from: 0.88, to: 1.12 },
      scaleY: { from: 1.1, to: 0.9 },
      yoyo: true,
      repeat: -1,
      duration: 110,
    });
    // Tip sparks shimmer.
    this.scene.tweens.add({
      targets: [iceCrystal, fireSpark],
      alpha: { from: 0.6, to: 1 },
      scale: { from: 0.7, to: 1.3 },
      yoyo: true,
      repeat: -1,
      duration: 120,
    });
    // Aura pulse.
    this.scene.tweens.add({
      targets: aura,
      scaleX: { from: 0.9, to: 1.15 },
      alpha: { from: 0.18, to: 0.34 },
      yoyo: true,
      repeat: -1,
      duration: 200,
    });
    // Eye glints.
    this.scene.tweens.add({
      targets: [blueEye, redEye],
      alpha: { from: 0.7, to: 1 },
      yoyo: true,
      repeat: -1,
      duration: 160,
    });
    // Trailing puffs flutter.
    this.scene.tweens.add({
      targets: [icePuff, firePuff],
      scaleX: { from: 0.7, to: 1.2 },
      alpha: { from: 0.5, to: 0.95 },
      yoyo: true,
      repeat: -1,
      duration: 140,
    });

    // Layer back-to-front: aura → trail puffs → maw → cones → cores → tips/eyes.
    this.visual.add([
      aura,
      icePuff, firePuff,
      maw,
      iceCone, fireCone,
      iceCore, fireCore,
      iceCrystal, fireSpark,
      blueEye, redEye,
    ]);
  }

  /** Sven Stormbolt — spinning purple warhammer with electric trails. */
  private buildStormHammer(): void {
    // Crackling purple aura around the hammer.
    const aura = this.scene.add.circle(0, 0, 14, 0xb84aff, 0.4);
    // Hammer handle — vertical purple shaft.
    const handle = this.scene.add.rectangle(0, 0, 2, 16, 0x8a4ad8)
      .setStrokeStyle(0.6, 0x4a2870, 1);
    // Hammer head — large rectangular block at one end.
    const head = this.scene.add.rectangle(0, -7, 12, 8, 0xa040ff)
      .setStrokeStyle(1, 0x4a2870, 1);
    // Hammer head highlight (brighter strip).
    const headHi = this.scene.add.rectangle(0, -8.5, 10, 1.6, 0xd080ff, 0.95);
    // Lightning rune glowing on the head face.
    const rune = this.scene.add.line(0, 0, -2, -8, 2, -6, 0xffffff, 1).setLineWidth(1.4).setOrigin(0, 0);
    const rune2 = this.scene.add.line(0, 0, 2, -6, -1, -4, 0xffffff, 1).setLineWidth(1.4).setOrigin(0, 0);
    // Pommel/cap at the bottom of the handle.
    const pommel = this.scene.add.circle(0, 8, 2.4, 0xd080ff).setStrokeStyle(0.6, 0x4a2870, 1);
    // Arcing electric sparks orbiting the hammer.
    const arc1 = this.scene.add.circle(7, 0, 1.4, 0xeac8ff, 1);
    const arc2 = this.scene.add.circle(-7, 2, 1.2, 0xffffff, 0.95);
    // The whole hammer container piece will spin via tween.
    // Group hammer bits so they all rotate together (use a sub-container).
    const hammer = this.scene.add.container(0, 0, [handle, pommel, head, headHi, rune, rune2]);
    // Hammer spin (independent of travel rotation).
    this.scene.tweens.add({
      targets: hammer,
      angle: "+=360",
      duration: 320,
      repeat: -1,
    });
    // Sparks orbit opposite direction.
    this.scene.tweens.add({
      targets: [arc1, arc2],
      angle: "-=360",
      duration: 280,
      repeat: -1,
    });
    // Aura pulse.
    this.scene.tweens.add({
      targets: aura,
      scale: { from: 0.9, to: 1.2 },
      alpha: { from: 0.3, to: 0.6 },
      yoyo: true,
      repeat: -1,
      duration: 200,
    });
    this.visual.add([aura, hammer, arc1, arc2]);
  }

  /** Mud — Witch Doctor's spinning bone-axe with mud splatter trail. */
  private buildBoneAxe(): void {
    // Mud splatter halo around the axe.
    const halo = this.scene.add.circle(0, 0, 13, 0x4a3520, 0.4);
    // Bone handle — long ivory shaft.
    const handle = this.scene.add.rectangle(0, 0, 2, 18, 0xeae0c4)
      .setStrokeStyle(0.5, 0x6a5a3a, 1);
    // Bone handle joints (knobs).
    const joint1 = this.scene.add.circle(0, -6, 1.6, 0xeae0c4).setStrokeStyle(0.5, 0x6a5a3a, 1);
    const joint2 = this.scene.add.circle(0, 6, 1.6, 0xeae0c4).setStrokeStyle(0.5, 0x6a5a3a, 1);
    // Curved blade on top — crescent polygon with sharp edge.
    const bladeTop = this.scene.add.polygon(0, -10,
      [-9, 0, -8, -3, 0, -5, 8, -3, 9, 0, 6, 2, 0, 1, -6, 2],
      0xb8a878,
    ).setStrokeStyle(1, 0x4a3a20, 1);
    // Blade highlight (sharp edge).
    const bladeHi = this.scene.add.line(0, -10, -8, -3, 8, -3, 0xffffff, 1).setLineWidth(1).setOrigin(0, 0);
    // Mirror crescent blade on bottom.
    const bladeBot = this.scene.add.polygon(0, 10,
      [-9, 0, -8, 3, 0, 5, 8, 3, 9, 0, 6, -2, 0, -1, -6, -2],
      0xb8a878,
    ).setStrokeStyle(1, 0x4a3a20, 1);
    const bladeBotHi = this.scene.add.line(0, 10, -8, 3, 8, 3, 0xffffff, 1).setLineWidth(1).setOrigin(0, 0);
    // Skull bead in the center.
    const skull = this.scene.add.circle(0, 0, 2.4, 0xeae0c4).setStrokeStyle(0.6, 0x4a3a20, 1);
    const skullEye1 = this.scene.add.circle(-0.7, -0.3, 0.5, 0x2a1a08, 1);
    const skullEye2 = this.scene.add.circle(0.7, -0.3, 0.5, 0x2a1a08, 1);
    // Mud splatter chunks orbiting.
    const mud1 = this.scene.add.circle(8, 6, 1.6, 0x7a5a3a, 0.95);
    const mud2 = this.scene.add.circle(-7, -5, 1.4, 0x4a3520, 0.9);
    // Group axe pieces so they spin together.
    const axe = this.scene.add.container(0, 0, [handle, joint1, joint2, bladeTop, bladeHi, bladeBot, bladeBotHi, skull, skullEye1, skullEye2]);
    // Axe spin.
    this.scene.tweens.add({
      targets: axe,
      angle: "+=360",
      duration: 400,
      repeat: -1,
    });
    // Mud chunks orbit.
    this.scene.tweens.add({
      targets: [mud1, mud2],
      angle: "+=360",
      duration: 500,
      repeat: -1,
    });
    // Halo pulse.
    this.scene.tweens.add({
      targets: halo,
      scale: { from: 0.9, to: 1.15 },
      alpha: { from: 0.3, to: 0.6 },
      yoyo: true,
      repeat: -1,
      duration: 280,
    });
    this.visual.add([halo, axe, mud1, mud2]);
  }

  /** Ice — six-pointed snowflake shuriken, spinning with frost trail. */
  private buildSnowflake(): void {
    // Outer pale-blue glow.
    const aura = this.scene.add.circle(0, 0, 13, 0xa6e3ff, 0.45);
    // Build the snowflake as 6 radiating arms from center, each with branches.
    // Snowflake group spins via container.
    const flake = this.scene.add.container(0, 0);
    const armColor = 0xeaf6ff;
    const armEdge = 0xffffff;
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const ex = Math.cos(a) * 11;
      const ey = Math.sin(a) * 11;
      // Main arm — line from center to tip.
      const arm = this.scene.add.line(0, 0, 0, 0, ex, ey, armColor, 1).setLineWidth(1.6).setOrigin(0, 0);
      // Pointed tip diamond.
      const tip = this.scene.add.triangle(ex, ey,
        Math.cos(a) * 3, Math.sin(a) * 3,
        Math.cos(a + Math.PI / 2) * 1.6, Math.sin(a + Math.PI / 2) * 1.6,
        Math.cos(a - Math.PI / 2) * 1.6, Math.sin(a - Math.PI / 2) * 1.6,
        0xc8edff,
      ).setStrokeStyle(0.5, armEdge, 1);
      // Two lateral branches at 60% out.
      const bx = Math.cos(a) * 6;
      const by = Math.sin(a) * 6;
      const branchLen = 3.5;
      const b1ex = bx + Math.cos(a + Math.PI / 3) * branchLen;
      const b1ey = by + Math.sin(a + Math.PI / 3) * branchLen;
      const b2ex = bx + Math.cos(a - Math.PI / 3) * branchLen;
      const b2ey = by + Math.sin(a - Math.PI / 3) * branchLen;
      const branch1 = this.scene.add.line(0, 0, bx, by, b1ex, b1ey, armColor, 1).setLineWidth(1).setOrigin(0, 0);
      const branch2 = this.scene.add.line(0, 0, bx, by, b2ex, b2ey, armColor, 1).setLineWidth(1).setOrigin(0, 0);
      flake.add([arm, branch1, branch2, tip]);
    }
    // Hexagonal core (bright bead at center).
    const core = this.scene.add.circle(0, 0, 3, 0xffffff, 1).setStrokeStyle(0.6, 0x88c0e0, 1);
    const coreSpot = this.scene.add.circle(0, 0, 1.4, 0x88c0e0, 1);
    flake.add([core, coreSpot]);
    // Frost flecks orbiting outside.
    const fleck1 = this.scene.add.rectangle(9, 3, 1.4, 1.4, 0xffffff, 1).setRotation(Math.PI / 4);
    const fleck2 = this.scene.add.rectangle(-7, -6, 1.2, 1.2, 0xeaf6ff, 0.9).setRotation(Math.PI / 3);
    const fleck3 = this.scene.add.rectangle(-4, 8, 1.1, 1.1, 0xc8edff, 0.85).setRotation(Math.PI / 6);
    // Snowflake shuriken spin.
    this.scene.tweens.add({
      targets: flake,
      angle: "+=360",
      duration: 280,
      repeat: -1,
    });
    // Flecks orbit slower opposite direction.
    this.scene.tweens.add({
      targets: [fleck1, fleck2, fleck3],
      angle: "-=360",
      duration: 700,
      repeat: -1,
    });
    // Aura breathing.
    this.scene.tweens.add({
      targets: aura,
      scale: { from: 0.85, to: 1.2 },
      alpha: { from: 0.35, to: 0.65 },
      yoyo: true,
      repeat: -1,
      duration: 220,
    });
    // Core twinkle.
    this.scene.tweens.add({
      targets: [core, coreSpot],
      scale: { from: 0.85, to: 1.2 },
      yoyo: true,
      repeat: -1,
      duration: 180,
    });
    this.visual.add([aura, flake, fleck1, fleck2, fleck3]);
  }

  /** Vine — Venomancer's living thorn whip. Curling forward-tipped tendril with leaves and thorns. */
  private buildVineWhip(): void {
    // Toxic green halo behind the whip.
    const halo = this.scene.add.ellipse(-2, 0, 24, 9, 0x4ea84a, 0.35);
    // Main whip body — segmented curve drawn as a row of overlapping ellipses.
    // Going forward (+x) the whip thickens at the tip and curls slightly.
    const seg1 = this.scene.add.ellipse(-10, 1.5, 5, 3, 0x2a6a2a).setStrokeStyle(0.6, 0x1a3a14, 1);
    const seg2 = this.scene.add.ellipse(-6, 0.8, 5.5, 3.4, 0x3a8a3a).setStrokeStyle(0.6, 0x1a3a14, 1);
    const seg3 = this.scene.add.ellipse(-2, 0, 6, 3.6, 0x4ea84a).setStrokeStyle(0.6, 0x1a3a14, 1);
    const seg4 = this.scene.add.ellipse(2, -0.5, 6.5, 3.8, 0x5ac85a).setStrokeStyle(0.6, 0x1a3a14, 1);
    const seg5 = this.scene.add.ellipse(6, -1, 6, 3.6, 0x6ec85a).setStrokeStyle(0.6, 0x1a3a14, 1);
    // Bulbous head/jaw at the tip — venomous bud opening forward.
    const bud = this.scene.add.polygon(11, -1.2,
      [4, 0, 2, -3.5, -2, -3, -3, 0, -2, 3, 2, 3.5],
      0x6ec85a,
    ).setStrokeStyle(1, 0x1a3a14, 1);
    // Inner sickly mouth (yellow-green).
    const maw = this.scene.add.ellipse(11, -1.2, 4.5, 2.5, 0xd4ff70, 0.95);
    // Sharp thorn fangs at the tip.
    const fang1 = this.scene.add.triangle(13.5, -2.5, 0, 0, -2, -1.5, -2, 1.5, 0xeae0c4).setStrokeStyle(0.4, 0x4a3a20, 1);
    const fang2 = this.scene.add.triangle(13.5, 0.2, 0, 0, -2, -1.5, -2, 1.5, 0xeae0c4).setStrokeStyle(0.4, 0x4a3a20, 1);
    // Leaf sprout sticking off the side of the body.
    const leaf1 = this.scene.add.polygon(-4, -3.5, [0, 0, -2, -2.5, 0, -4, 2, -2], 0x6ec85a).setStrokeStyle(0.5, 0x1a3a14, 1);
    const leaf2 = this.scene.add.polygon(0, 3.5, [0, 0, -2, 2.5, 0, 4, 2, 2], 0x4ea84a).setStrokeStyle(0.5, 0x1a3a14, 1);
    // Side thorns.
    const thorn1 = this.scene.add.triangle(-2, -2.5, 0, 0, -1.5, 1.5, 1.5, 1.5, 0x2a4a1a);
    const thorn2 = this.scene.add.triangle(4, 2.2, 0, 0, -1.5, -1.5, 1.5, -1.5, 0x2a4a1a);
    // Venom drip from fangs.
    const drip = this.scene.add.ellipse(13.5, 2, 1.4, 2.2, 0xd4ff70, 0.9);
    // Whip slither — segments wave up/down independently.
    this.scene.tweens.add({
      targets: [seg1, seg3, seg5],
      y: "+=1.5",
      yoyo: true,
      repeat: -1,
      duration: 220,
      ease: "Sine.easeInOut",
    });
    this.scene.tweens.add({
      targets: [seg2, seg4],
      y: "-=1.5",
      yoyo: true,
      repeat: -1,
      duration: 220,
      ease: "Sine.easeInOut",
    });
    // Bud snap.
    this.scene.tweens.add({
      targets: [bud, maw],
      scaleX: { from: 0.85, to: 1.15 },
      yoyo: true,
      repeat: -1,
      duration: 180,
    });
    // Halo pulse.
    this.scene.tweens.add({
      targets: halo,
      scale: { from: 0.9, to: 1.15 },
      alpha: { from: 0.25, to: 0.5 },
      yoyo: true,
      repeat: -1,
      duration: 260,
    });
    // Drip stretch.
    this.scene.tweens.add({
      targets: drip,
      scaleY: { from: 0.7, to: 1.3 },
      yoyo: true,
      repeat: -1,
      duration: 200,
    });
    this.visual.add([halo, seg1, seg2, seg3, seg4, seg5, leaf1, leaf2, thorn1, thorn2, bud, maw, fang1, fang2, drip]);
  }

  /** Water — cresting tidal wave. Curling crescent of seawater with foam crest. */
  private buildTidalWave(): void {
    // Wide deep-ocean glow halo behind the wave.
    const halo = this.scene.add.ellipse(-2, 1, 28, 16, 0x1c5a8a, 0.45);
    // Main wave body — curling crescent (polygon shaped like a breaking wave).
    // Forward (+x) is the curl tip, back is the trough.
    const body = this.scene.add.polygon(0, 0,
      [10, 0, 8, -7, 4, -9, -2, -8, -8, -5, -10, 0, -8, 4, -2, 6, 4, 5, 8, 2],
      0x2a8acf,
    ).setStrokeStyle(1.2, 0x1c5a8a, 1);
    // Mid-wave bright cyan layer (shows volume).
    const mid = this.scene.add.polygon(1, 0,
      [8, -1, 5, -6, 0, -7, -5, -5, -7, -1, -5, 3, 0, 4, 4, 3, 7, 1],
      0x4cc9f0,
    );
    // Inner brightest cyan-white highlight.
    const inner = this.scene.add.polygon(2, -1,
      [6, -1, 3, -4, -2, -4, -5, -2, -3, 1, 1, 2, 4, 1],
      0xb6efff,
      0.9,
    );
    // Foam crest curling at top — three white blobs along the crest.
    const foam1 = this.scene.add.circle(6, -6, 2.6, 0xffffff, 1);
    const foam2 = this.scene.add.circle(2, -8, 3, 0xffffff, 1);
    const foam3 = this.scene.add.circle(-3, -7, 2.4, 0xffffff, 1);
    // Spray droplets flying ahead and above the curl.
    const spray1 = this.scene.add.circle(11, -4, 1.4, 0xeaf6ff, 1);
    const spray2 = this.scene.add.circle(13, -1, 1.1, 0xb6efff, 0.9);
    const spray3 = this.scene.add.circle(9, -10, 1, 0xffffff, 0.85);
    // Wave undulation — vertical bobbing.
    this.scene.tweens.add({
      targets: [body, mid, inner],
      y: { from: -1, to: 1 },
      scaleY: { from: 0.92, to: 1.08 },
      yoyo: true,
      repeat: -1,
      duration: 220,
      ease: "Sine.easeInOut",
    });
    // Foam crest flicker.
    this.scene.tweens.add({
      targets: [foam1, foam2, foam3],
      scale: { from: 0.85, to: 1.2 },
      alpha: { from: 0.7, to: 1 },
      yoyo: true,
      repeat: -1,
      duration: 160,
    });
    // Spray droplets bob.
    this.scene.tweens.add({
      targets: [spray1, spray2, spray3],
      y: "+=2",
      alpha: { from: 0.5, to: 1 },
      yoyo: true,
      repeat: -1,
      duration: 200,
    });
    // Halo pulse.
    this.scene.tweens.add({
      targets: halo,
      scale: { from: 0.9, to: 1.1 },
      yoyo: true,
      repeat: -1,
      duration: 240,
    });
    this.visual.add([halo, body, mid, inner, foam1, foam2, foam3, spray3, spray1, spray2]);
  }

  /** Earth — boulder rocket. Pointed stone projectile with fiery exhaust trail. */
  private buildBoulderRocket(): void {
    // Exhaust flame plume behind (negative x = trailing).
    const exhaustOuter = this.scene.add.ellipse(-12, 0, 16, 8, 0xff8a3a, 0.7);
    const exhaustMid = this.scene.add.ellipse(-10, 0, 12, 5, 0xffb84a, 0.95);
    const exhaustCore = this.scene.add.ellipse(-9, 0, 9, 2.5, 0xfff2a8, 1);
    // Smoke puff trailing further behind.
    const smoke1 = this.scene.add.circle(-15, -1, 2.4, 0x6a5040, 0.6);
    const smoke2 = this.scene.add.circle(-17, 2, 2, 0x4a3a30, 0.5);
    // Dust aura wrapping the boulder.
    const dust = this.scene.add.ellipse(0, 0, 18, 11, 0xb08a4f, 0.35);
    // Main pointed boulder body — forward-pointing irregular polygon.
    const body = this.scene.add.polygon(0, 0,
      [9, 0, 6, -5, 0, -6, -6, -4, -8, 0, -6, 4, 0, 6, 6, 5],
      0x8a6840,
    ).setStrokeStyle(1.2, 0x5d4530, 1);
    // Cracks across the boulder (lighter veins).
    const crack1 = this.scene.add.line(0, 0, -4, -3, 5, 1, 0x5d4530, 1).setLineWidth(1).setOrigin(0, 0);
    const crack2 = this.scene.add.line(0, 0, -2, 3, 4, -2, 0x5d4530, 1).setLineWidth(0.8).setOrigin(0, 0);
    // Lighter chunks (gravel detail).
    const chunk1 = this.scene.add.rectangle(-2, -3, 2.5, 2, 0xb08a4f, 0.85);
    const chunk2 = this.scene.add.rectangle(3, 3, 2, 1.6, 0xb08a4f, 0.85);
    // Highlight on top edge.
    const highlight = this.scene.add.line(0, 0, -4, -4, 4, -4.5, 0xd4a878, 0.9).setLineWidth(1).setOrigin(0, 0);
    // Bright tip — leading edge spark.
    const tipSpark = this.scene.add.circle(9, 0, 1.6, 0xfff2a8, 1);
    // Exhaust pulse — fire flutter.
    this.scene.tweens.add({
      targets: [exhaustOuter, exhaustMid, exhaustCore],
      scaleX: { from: 0.7, to: 1.25 },
      scaleY: { from: 0.85, to: 1.15 },
      alpha: { from: 0.6, to: 1 },
      yoyo: true,
      repeat: -1,
      duration: 80,
    });
    // Smoke drift back.
    this.scene.tweens.add({
      targets: [smoke1, smoke2],
      x: "-=2",
      alpha: { from: 0.3, to: 0.7 },
      yoyo: true,
      repeat: -1,
      duration: 220,
    });
    // Dust aura subtle pulse.
    this.scene.tweens.add({
      targets: dust,
      scale: { from: 0.95, to: 1.1 },
      yoyo: true,
      repeat: -1,
      duration: 280,
    });
    // Tip spark flicker.
    this.scene.tweens.add({
      targets: tipSpark,
      scale: { from: 0.7, to: 1.3 },
      yoyo: true,
      repeat: -1,
      duration: 110,
    });
    this.visual.add([smoke2, smoke1, exhaustOuter, exhaustMid, exhaustCore, dust, body, crack1, crack2, chunk1, chunk2, highlight, tipSpark]);
  }

  /** Rattletrap (brew) — FLAMING METEOR: charred rock core engulfed in a
   *  massive whirling fire-shroud with long flame tail and ember halo. */
  private buildBrewJug(): void {
    // ── Massive trailing flame tail (behind, fans out wider than meteor) ──
    const tailOuter = this.scene.add.ellipse(-22, 0, 38, 18, 0xff5a1a, 0.55);
    const tailMid = this.scene.add.ellipse(-16, 0, 28, 13, 0xff8a3a, 0.85);
    const tailHot = this.scene.add.ellipse(-11, 0, 20, 8, 0xffd060, 0.95);
    const tailCore = this.scene.add.ellipse(-7, 0, 14, 4, 0xfff2a8, 1);
    // ── Outer fire shroud (envelops the entire meteor) ──
    const shroudOuter = this.scene.add.circle(0, 0, 18, 0xff5a1a, 0.45);
    const shroudMid = this.scene.add.circle(0, 0, 14, 0xff8a3a, 0.65);
    const shroudHot = this.scene.add.circle(0, 0, 11, 0xffb84a, 0.85);
    // ── Tongues of flame licking outward in 6 directions ──
    const tongues: Phaser.GameObjects.GameObject[] = [];
    for (let i = 0; i < 6; i++) {
      const ang = (i / 6) * Math.PI * 2;
      const dx = Math.cos(ang) * 11;
      const dy = Math.sin(ang) * 11;
      const tongue = this.scene.add.ellipse(dx, dy, 9, 4.5, 0xff8a3a, 0.85);
      tongue.setRotation(ang);
      tongues.push(tongue);
    }
    // ── Meteor core: charred dark-red rock with cracks ──
    const rockShadow = this.scene.add.circle(0.5, 0.5, 7, 0x1a0a0a, 0.7);
    const rock = this.scene.add.circle(0, 0, 7, 0x4a2818).setStrokeStyle(0.8, 0x1a0a0a, 1);
    // Charred surface highlight (cooled crust).
    const crust = this.scene.add.circle(-1.5, -1.5, 3.5, 0x6a3a20, 0.85);
    // Glowing magma cracks across the rock.
    const crack1 = this.scene.add.rectangle(0, -1, 8, 0.9, 0xffb84a, 1);
    crack1.setRotation(0.4);
    const crack2 = this.scene.add.rectangle(0, 2, 6, 0.7, 0xff5a1a, 1);
    crack2.setRotation(-0.6);
    const crack3 = this.scene.add.rectangle(-1, 0, 4, 0.6, 0xffd060, 1);
    crack3.setRotation(1.2);
    // White-hot leading edge (front face glowing from atmospheric heat).
    const leadingEdge = this.scene.add.ellipse(3, 0, 5, 7, 0xfff2a8, 0.9);
    // ── Spark embers shooting forward from the leading edge ──
    const spark1 = this.scene.add.circle(8, -2, 1.0, 0xffd060, 1);
    const spark2 = this.scene.add.circle(9, 1.5, 0.8, 0xfff2a8, 1);
    const spark3 = this.scene.add.circle(7, 3, 0.7, 0xff8a3a, 1);
    // ── Animations ──
    // Tail flicker — fast.
    this.scene.tweens.add({
      targets: [tailOuter, tailMid, tailHot, tailCore],
      scaleX: { from: 0.7, to: 1.25 },
      scaleY: { from: 0.8, to: 1.2 },
      alpha: { from: 0.5, to: 1 },
      yoyo: true, repeat: -1, duration: 80,
    });
    // Shroud pulse — engulfing fire breathes.
    this.scene.tweens.add({
      targets: [shroudOuter, shroudMid, shroudHot],
      scale: { from: 0.85, to: 1.18 },
      alpha: { from: 0.55, to: 0.95 },
      yoyo: true, repeat: -1, duration: 110,
    });
    // Tongues dance.
    this.scene.tweens.add({
      targets: tongues,
      scaleX: { from: 0.6, to: 1.3 },
      alpha: { from: 0.5, to: 1 },
      yoyo: true, repeat: -1, duration: 130,
    });
    // Crack glow pulse — magma breathing.
    this.scene.tweens.add({
      targets: [crack1, crack2, crack3],
      alpha: { from: 0.6, to: 1 },
      yoyo: true, repeat: -1, duration: 160,
    });
    // Leading edge flicker.
    this.scene.tweens.add({
      targets: leadingEdge,
      scale: { from: 0.9, to: 1.2 },
      alpha: { from: 0.7, to: 1 },
      yoyo: true, repeat: -1, duration: 100,
    });
    // Spark twinkle.
    this.scene.tweens.add({
      targets: [spark1, spark2, spark3],
      alpha: { from: 0.4, to: 1 },
      scale: { from: 0.7, to: 1.4 },
      yoyo: true, repeat: -1, duration: 120,
    });
    this.visual.add([
      tailOuter, tailMid, tailHot, tailCore,
      shroudOuter, shroudMid, shroudHot,
      ...tongues,
      rockShadow, rock, crust, crack1, crack2, crack3, leadingEdge,
      spark1, spark2, spark3,
    ]);
  }

  /** Pugna Decrepify — green soul wisp with trailing soul tendrils. */
  private buildSoulWisp(): void {
    // Outer purple-green glow.
    const aura = this.scene.add.circle(0, 0, 14, 0xb84aff, 0.4);
    // Sickly-green inner halo.
    const innerHalo = this.scene.add.circle(0, 0, 9, 0x6ee0a0, 0.55);
    // Floating skull-like head — small ghostly polygon.
    const skullHead = this.scene.add.polygon(0, -1,
      [-4, -3, -3, -5, 3, -5, 4, -3, 4, 1, 3, 4, -3, 4, -4, 1],
      0xeaf8e8,
    ).setStrokeStyle(0.8, 0x4a8a6a, 1);
    // Skull eye sockets — glowing green.
    const eye1 = this.scene.add.circle(-1.6, -1, 1, 0x6ee0a0, 1);
    const eye2 = this.scene.add.circle(1.6, -1, 1, 0x6ee0a0, 1);
    // Bright soul flame core inside the head.
    const soulCore = this.scene.add.circle(0, 0.5, 1.4, 0xffffff, 1);
    // Trailing soul tendrils — three wispy curves behind.
    const tendril1 = this.scene.add.ellipse(-7, -2, 8, 3, 0x6ee0a0, 0.7);
    const tendril2 = this.scene.add.ellipse(-9, 1, 9, 2.5, 0xb84aff, 0.65);
    const tendril3 = this.scene.add.ellipse(-6, 3, 6, 2, 0xeaf8e8, 0.6);
    // Tiny soul motes orbiting.
    const mote1 = this.scene.add.circle(6, -3, 1, 0x6ee0a0, 1);
    const mote2 = this.scene.add.circle(-3, 6, 0.9, 0xb84aff, 1);
    const mote3 = this.scene.add.circle(5, 5, 0.8, 0xffffff, 1);
    // Tendrils undulate.
    this.scene.tweens.add({
      targets: [tendril1, tendril2, tendril3],
      scaleX: { from: 0.8, to: 1.2 },
      alpha: { from: 0.4, to: 0.85 },
      yoyo: true,
      repeat: -1,
      duration: 240,
    });
    // Eye flicker.
    this.scene.tweens.add({
      targets: [eye1, eye2],
      alpha: { from: 0.5, to: 1 },
      yoyo: true,
      repeat: -1,
      duration: 180,
    });
    // Soul core pulse.
    this.scene.tweens.add({
      targets: soulCore,
      scale: { from: 0.7, to: 1.4 },
      yoyo: true,
      repeat: -1,
      duration: 200,
    });
    // Halos pulse.
    this.scene.tweens.add({
      targets: [aura, innerHalo],
      scale: { from: 0.85, to: 1.2 },
      yoyo: true,
      repeat: -1,
      duration: 280,
    });
    // Motes orbit.
    this.scene.tweens.add({
      targets: [mote1, mote2, mote3],
      angle: "+=360",
      duration: 700,
      repeat: -1,
    });
    this.visual.add([aura, innerHalo, tendril1, tendril2, tendril3, skullHead, eye1, eye2, soulCore, mote1, mote2, mote3]);
  }

  /** Beastmaster Wild Axes — pair of spinning twin axes. */
  private buildBeastAxes(): void {
    // Brown blood-mist halo.
    const halo = this.scene.add.circle(0, 0, 14, 0x8a5028, 0.4);
    // Two axes orbit a center point — separate sub-containers so we can animate independently.
    const axe1 = this.makeWildAxe();
    axe1.setPosition(7, 0);
    const axe2 = this.makeWildAxe();
    axe2.setPosition(-7, 0);
    axe2.setRotation(Math.PI);
    // Both axes spin.
    this.scene.tweens.add({
      targets: [axe1, axe2],
      angle: "+=360",
      duration: 280,
      repeat: -1,
    });
    // The pair of axes orbit around the center.
    const orbit = this.scene.add.container(0, 0, [axe1, axe2]);
    this.scene.tweens.add({
      targets: orbit,
      angle: "+=360",
      duration: 520,
      repeat: -1,
    });
    // Halo pulse.
    this.scene.tweens.add({
      targets: halo,
      scale: { from: 0.9, to: 1.15 },
      yoyo: true,
      repeat: -1,
      duration: 260,
    });
    this.visual.add([halo, orbit]);
  }

  /** Builds one small thrown axe used by buildBeastAxes. */
  private makeWildAxe(): Phaser.GameObjects.Container {
    const handle = this.scene.add.rectangle(0, 0, 1.6, 10, 0x6a4a28).setStrokeStyle(0.4, 0x3a2418, 1);
    // Crescent blade head.
    const blade = this.scene.add.polygon(0, -5,
      [-5, 0, -4, -2, 4, -2, 5, 0, 3, 1, 0, 1, -3, 1],
      0xc8b888,
    ).setStrokeStyle(0.6, 0x4a3a20, 1);
    const bladeEdge = this.scene.add.line(0, -5, -5, 0, 5, 0, 0xffffff, 1).setLineWidth(0.8).setOrigin(0, 0);
    // Pommel knot at the bottom.
    const knot = this.scene.add.circle(0, 5, 1.4, 0x8a5a30).setStrokeStyle(0.4, 0x3a2418, 1);
    return this.scene.add.container(0, 0, [handle, blade, bladeEdge, knot]);
  }

  /** Tidehunter Ravage — long dark-green tentacle whipping forward, suckers visible. */
  private buildTentacle(): void {
    // Dark sea-water glow trailing behind.
    const halo = this.scene.add.ellipse(-2, 0, 30, 12, 0x1a4a3a, 0.4);
    // Tentacle body — chain of segmented ellipses growing from thin tail to thick head.
    // Forward (+x) is the head.
    const seg1 = this.scene.add.ellipse(-12, 1.5, 4, 2.5, 0x1a4a3a).setStrokeStyle(0.5, 0x0a2a18, 1);
    const seg2 = this.scene.add.ellipse(-9, 0.8, 5, 3.2, 0x2a6a4a).setStrokeStyle(0.5, 0x0a2a18, 1);
    const seg3 = this.scene.add.ellipse(-5, 0, 6, 4, 0x3a8a5a).setStrokeStyle(0.6, 0x0a2a18, 1);
    const seg4 = this.scene.add.ellipse(-1, -0.6, 7, 4.6, 0x4ab06a).setStrokeStyle(0.6, 0x0a2a18, 1);
    const seg5 = this.scene.add.ellipse(4, -1.2, 8, 5.2, 0x5ac888).setStrokeStyle(0.6, 0x0a2a18, 1);
    // Suckers — small pale circles on the underside of each segment.
    const sucker1 = this.scene.add.circle(-12, 3, 0.8, 0xeac8a0, 1);
    const sucker2 = this.scene.add.circle(-8, 2.6, 1, 0xeac8a0, 1);
    const sucker3 = this.scene.add.circle(-4, 2.2, 1.2, 0xeac8a0, 1);
    const sucker4 = this.scene.add.circle(0, 1.6, 1.4, 0xeac8a0, 1);
    const sucker5 = this.scene.add.circle(4, 1, 1.6, 0xeac8a0, 1);
    // Ring inside each sucker.
    const sring1 = this.scene.add.circle(-12, 3, 0.4, 0x6a3a18, 1);
    const sring2 = this.scene.add.circle(-8, 2.6, 0.5, 0x6a3a18, 1);
    const sring3 = this.scene.add.circle(-4, 2.2, 0.6, 0x6a3a18, 1);
    const sring4 = this.scene.add.circle(0, 1.6, 0.7, 0x6a3a18, 1);
    const sring5 = this.scene.add.circle(4, 1, 0.8, 0x6a3a18, 1);
    // Curling head tip — pointed bulb forward.
    const head = this.scene.add.polygon(10, -2,
      [4, 0, 2, -3, -2, -4, -3, -2, -4, 1, -2, 3, 2, 3],
      0x6ad898,
    ).setStrokeStyle(1, 0x0a2a18, 1);
    // Bright reflective sheen on head top.
    const headHi = this.scene.add.ellipse(8, -3.5, 4, 1.6, 0xb6efd0, 0.85);
    // Sharp curling tip claw at the front.
    const claw = this.scene.add.triangle(13, -3, 0, 0, -3, -1.5, -2, 1.5, 0x4a8a3a).setStrokeStyle(0.5, 0x0a2a18, 1);
    // Wisps of dark water trailing behind.
    const wisp1 = this.scene.add.ellipse(-14, 2, 8, 2, 0x2a8a6a, 0.6);
    const wisp2 = this.scene.add.ellipse(-16, -1, 6, 1.6, 0x4ab884, 0.5);
    // Tentacle slither — segments wave (alternating up/down for serpentine motion).
    this.scene.tweens.add({
      targets: [seg1, seg3, seg5, sucker1, sucker3, sucker5, sring1, sring3, sring5],
      y: "+=1.6",
      yoyo: true,
      repeat: -1,
      duration: 220,
      ease: "Sine.easeInOut",
    });
    this.scene.tweens.add({
      targets: [seg2, seg4, sucker2, sucker4, sring2, sring4],
      y: "-=1.6",
      yoyo: true,
      repeat: -1,
      duration: 220,
      ease: "Sine.easeInOut",
    });
    // Head squeeze.
    this.scene.tweens.add({
      targets: [head, headHi, claw],
      scaleX: { from: 0.85, to: 1.2 },
      yoyo: true,
      repeat: -1,
      duration: 200,
    });
    // Wisps undulate.
    this.scene.tweens.add({
      targets: [wisp1, wisp2],
      scaleX: { from: 0.85, to: 1.2 },
      alpha: { from: 0.3, to: 0.7 },
      yoyo: true,
      repeat: -1,
      duration: 240,
    });
    // Halo pulse.
    this.scene.tweens.add({
      targets: halo,
      scale: { from: 0.9, to: 1.15 },
      yoyo: true,
      repeat: -1,
      duration: 280,
    });
    this.visual.add([halo, wisp2, wisp1, seg1, seg2, seg3, seg4, seg5, sucker1, sring1, sucker2, sring2, sucker3, sring3, sucker4, sring4, sucker5, sring5, head, headHi, claw]);
  }

  /** Mirana Sacred Arrow — silver shaft, steel head, element-tinted fletching + moon halo. */
  /** Necrolyte death scythe — large rotating crescent blade with green soul-glow edge. */
  private buildDeathScythe(): void {
    // ROTUND'JERE FIDGET SPINNER — 3-arm necrotic-green tri-spinner.
    // Center bearing + 3 radiating arms + 3 outer bearings, all spinning fast.
    // Self-spin tween on this.visual; not direction-rotated by update().
    const ARM_LEN = 13;           // distance from center to outer bearing
    const OUTER_R = 5.5;          // outer bearing radius
    const HUB_R = 4.5;            // center bearing radius

    // Soft necrotic aura behind the spinner.
    const auraOuter = this.scene.add.circle(0, 0, 24, 0x1a3a20, 0.22);
    const aura = this.scene.add.circle(0, 0, 18, 0x6aff8a, 0.30);

    // Three connecting arms (dark steel body w/ green edge).
    const arms: Phaser.GameObjects.GameObject[] = [];
    const lobes: Phaser.GameObjects.GameObject[] = [];
    for (let i = 0; i < 3; i++) {
      const ang = (i * Math.PI * 2) / 3 - Math.PI / 2;  // start pointing up
      const cx = (Math.cos(ang) * ARM_LEN) / 2;
      const cy = (Math.sin(ang) * ARM_LEN) / 2;

      // Arm body — dark backing.
      const armBack = this.scene.add.rectangle(cx, cy, ARM_LEN + 2, 7, 0x14281a)
        .setStrokeStyle(0.6, 0x000, 1);
      armBack.setRotation(ang);
      // Arm inlay — emerald glow strip.
      const armGlow = this.scene.add.rectangle(cx, cy, ARM_LEN, 2.2, 0x6aff8a, 0.9);
      armGlow.setRotation(ang);
      arms.push(armBack, armGlow);

      // Outer bearing lobe at arm tip.
      const ox = Math.cos(ang) * ARM_LEN;
      const oy = Math.sin(ang) * ARM_LEN;
      const lobeOuter = this.scene.add.circle(ox, oy, OUTER_R + 1.2, 0x14281a)
        .setStrokeStyle(0.7, 0x000, 1);
      const lobe = this.scene.add.circle(ox, oy, OUTER_R, 0x2a8a4a)
        .setStrokeStyle(0.6, 0x6aff8a, 1);
      // Inner bearing hole — bright green pip.
      const lobeHole = this.scene.add.circle(ox, oy, OUTER_R * 0.45, 0xeafff0, 0.95);
      const lobePip = this.scene.add.circle(ox, oy, OUTER_R * 0.20, 0x6aff8a, 1);
      lobes.push(lobeOuter, lobe, lobeHole, lobePip);
    }

    // Center hub — bearing with bright eye.
    const hubRing = this.scene.add.circle(0, 0, HUB_R + 1.3, 0x14281a)
      .setStrokeStyle(0.7, 0x000, 1);
    const hub = this.scene.add.circle(0, 0, HUB_R, 0x2a8a4a)
      .setStrokeStyle(0.6, 0x6aff8a, 1);
    const hubInner = this.scene.add.circle(0, 0, HUB_R * 0.55, 0xeafff0, 0.95);
    const hubEye = this.scene.add.circle(0, 0, HUB_R * 0.28, 0x6aff8a, 1);

    // Aura breathing.
    this.scene.tweens.add({
      targets: aura,
      scale: { from: 0.88, to: 1.15 },
      alpha: { from: 0.22, to: 0.50 },
      yoyo: true, repeat: -1, duration: 260,
    });
    // Hub eye pulse.
    this.scene.tweens.add({
      targets: hubEye,
      alpha: { from: 0.55, to: 1 },
      scale: { from: 0.8, to: 1.3 },
      yoyo: true, repeat: -1, duration: 200,
    });
    // FAST FIDGET SPIN — ~4 rev/s.
    this.scene.tweens.add({
      targets: this.visual,
      angle: 360,
      duration: 250,
      repeat: -1,
      ease: "Linear",
    });

    this.visual.add([
      auraOuter, aura,
      ...arms,
      hubRing, hub, hubInner, hubEye,
      ...lobes,
    ]);
  }

  /** Phantom Assassin — SPINNING PURPLE CROSS-BLADE (buzz-saw glaive that
   *  cuts through enemies). Two perpendicular curved blades forming an X,
   *  spinning continuously during flight. Self-rotates — NOT direction-locked. */
  private buildPhantomBlade(): void {
    // Dark amethyst aura disc behind everything (the rotating wake). BIGGER.
    const auraOuter = this.scene.add.circle(0, 0, 28, 0x4a1a7a, 0.18);
    const aura = this.scene.add.circle(0, 0, 22, 0x6a2a9a, 0.32);
    // Inner purple core glow.
    const core = this.scene.add.circle(0, 0, 9, 0xd4a0ff, 0.90);
    const coreHot = this.scene.add.circle(0, 0, 4.5, 0xffffff, 0.95);
    // FOUR blade arms forming a cross (X-shape buzz-saw of soul-blades).
    // Each arm: amethyst body + white-hot cutting edge + sharp tip. BIGGER.
    const blades: Phaser.GameObjects.GameObject[] = [];
    for (let i = 0; i < 4; i++) {
      const ang = (i * Math.PI) / 2;        // 0, 90, 180, 270
      const cx = Math.cos(ang) * 15;
      const cy = Math.sin(ang) * 15;
      // Blade body — wider violet rectangle pointing outward.
      const bladeBody = this.scene.add.rectangle(cx, cy, 28, 4.8, 0x8a4ac8).setStrokeStyle(0.8, 0x2a0a4a, 1);
      bladeBody.setRotation(ang);
      // Bright white cutting edge on the leading face.
      const edge = this.scene.add.rectangle(cx, cy, 28, 1.4, 0xffffff, 1);
      edge.setRotation(ang);
      // Sharp tip — pointed triangle at the outer end.
      const tipX = Math.cos(ang) * 29;
      const tipY = Math.sin(ang) * 29;
      const tip = this.scene.add.triangle(tipX, tipY, 0, -3, 9, 0, 0, 3, 0xd4a0ff).setStrokeStyle(0.6, 0xffffff, 1);
      tip.setRotation(ang);
      blades.push(bladeBody, edge, tip);
    }
    // Central amethyst gem on top of the hub.
    const gem = this.scene.add.polygon(0, 0, [0, -4.5, 4.5, 0, 0, 4.5, -4.5, 0], 0xd4a0ff).setStrokeStyle(0.6, 0xffffff, 1);
    // Sparkle motes orbiting the hub.
    const spark1 = this.scene.add.circle(10, 0, 1.4, 0xffffff, 1);
    const spark2 = this.scene.add.circle(-10, 0, 1.4, 0xd4a0ff, 1);
    // Aura breathing.
    this.scene.tweens.add({
      targets: aura,
      scale: { from: 0.85, to: 1.15 },
      alpha: { from: 0.20, to: 0.45 },
      yoyo: true, repeat: -1, duration: 220,
    });
    // Core hot-flicker.
    this.scene.tweens.add({
      targets: coreHot,
      alpha: { from: 0.6, to: 1 },
      scale: { from: 0.8, to: 1.3 },
      yoyo: true, repeat: -1, duration: 140,
    });
    // Spark twinkle.
    this.scene.tweens.add({
      targets: [spark1, spark2],
      alpha: { from: 0.3, to: 1 },
      scale: { from: 0.6, to: 1.4 },
      yoyo: true, repeat: -1, duration: 180,
    });
    // CONTINUOUS SPIN — rotate the whole visual container very fast (~5 rev/s).
    this.scene.tweens.add({
      targets: this.visual,
      rotation: Math.PI * 2,
      duration: 200,
      repeat: -1,
      ease: "Linear",
    });
    this.visual.add([auraOuter, aura, core, coreHot, ...blades, gem, spark1, spark2]);
  }

  /**
   * Sven (forest_fire) — VOID SPINNING BLADE: a twin-fan buzzsaw of bright
   * violet energy blades whirling around a glowing purple core. Distinct from
   * the phantom's 4-arm cross — this is a 2-arm double-edged saw with a wide
   * energy disc and a counter-spinning inner halo.
   */
  private buildVoidBlade(): void {
    // Wide violet energy disc (the spinning wake).
    const discOuter = this.scene.add.circle(0, 0, 26, 0x4a1a7a, 0.20);
    const disc = this.scene.add.circle(0, 0, 19, 0x8a3ac8, 0.34);
    // Glowing core.
    const core = this.scene.add.circle(0, 0, 8.5, 0xd4a0ff, 0.92);
    const coreHot = this.scene.add.circle(0, 0, 4, 0xffffff, 0.95);
    // TWO long double-edged blades forming a single fan (180° apart).
    const blades: Phaser.GameObjects.GameObject[] = [];
    for (let i = 0; i < 2; i++) {
      const ang = i * Math.PI; // 0, 180 — one straight saw bar
      // Long violet blade body.
      const body = this.scene.add.rectangle(0, 0, 56, 6, 0xb84aff).setStrokeStyle(0.8, 0x2a0a4a, 1);
      body.setRotation(ang);
      // White-hot cutting edge.
      const edge = this.scene.add.rectangle(0, 0, 56, 1.8, 0xffffff, 1);
      edge.setRotation(ang);
      // Sharp pointed tip.
      const tipX = Math.cos(ang) * 30;
      const tipY = Math.sin(ang) * 30;
      const tip = this.scene.add.triangle(tipX, tipY, 0, -4, 11, 0, 0, 4, 0xd4a0ff).setStrokeStyle(0.6, 0xffffff, 1);
      tip.setRotation(ang);
      blades.push(body, edge, tip);
    }
    // Counter-spinning inner halo ring (added to its own sub-container for
    // independent rotation).
    const halo = this.scene.add.circle(0, 0, 13, 0x000000, 0).setStrokeStyle(2, 0xd080ff, 0.8);
    // Central violet gem.
    const gem = this.scene.add.polygon(0, 0, [0, -5, 5, 0, 0, 5, -5, 0], 0xffffff).setStrokeStyle(0.6, 0xb84aff, 1);
    // Disc breathing.
    this.scene.tweens.add({
      targets: disc,
      scale: { from: 0.85, to: 1.15 },
      alpha: { from: 0.22, to: 0.46 },
      yoyo: true, repeat: -1, duration: 200,
    });
    // Core flicker.
    this.scene.tweens.add({
      targets: coreHot,
      alpha: { from: 0.6, to: 1 },
      scale: { from: 0.8, to: 1.3 },
      yoyo: true, repeat: -1, duration: 130,
    });
    // Halo counter-spin.
    this.scene.tweens.add({
      targets: halo,
      rotation: -Math.PI * 2,
      duration: 600,
      repeat: -1,
      ease: "Linear",
    });
    // VERY FAST continuous spin of the whole saw (~5.5 rev/s).
    this.scene.tweens.add({
      targets: this.visual,
      rotation: Math.PI * 2,
      duration: 180,
      repeat: -1,
      ease: "Linear",
    });
    this.visual.add([discOuter, disc, halo, core, coreHot, ...blades, gem]);
  }

  /** Luna moon tornado — instant golden laser beam wrapped in a spiraling tornado. */
  private fireMoonTornado(sx: number, sy: number): void {
    const now = this.scene.time.now;
    const tx = this.target.position.x;
    const ty = this.target.position.y;
    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.max(1, Math.hypot(dx, dy));
    const ang = Math.atan2(dy, dx);
    const px = -dy / len; // perpendicular unit
    const py = dx / len;

    // ---- MOON TORNADO (no laser beam) — a spiraling funnel of moon energy
    // that twists from the caster onto the target. The straight beam is gone;
    // a thrown crescent glaive leads the strike instead. ----
    // Crescent moon-glaive flung along the path (spinning), reads as a thrown
    // blade rather than a beam.
    const glaive = this.scene.add.container(sx, sy);
    const gBody = this.scene.add.arc(0, 0, 9, 200, 340, false, 0xfff2a8, 0).setStrokeStyle(4, 0xffe070, 1);
    const gCore = this.scene.add.arc(0, 0, 9, 210, 330, false, 0xffffff, 0).setStrokeStyle(1.6, 0xffffff, 1);
    glaive.add([gBody, gCore]);
    glaive.setDepth(307);
    this.scene.tweens.add({
      targets: glaive,
      x: tx, y: ty,
      rotation: Math.PI * 4,
      duration: 220, ease: "Quad.easeIn",
      onComplete: () => glaive.destroy(),
    });

    // ---- Tornado spiral particles twisting along the path (funnel, not beam) ----
    const turns = 5;
    const particles: Phaser.GameObjects.GameObject[] = [];
    const sampleCount = Math.max(8, Math.floor(len / 8));
    for (let i = 0; i <= sampleCount; i++) {
      const t = i / sampleCount;
      const cx = sx + dx * t;
      const cy = sy + dy * t;
      const phase = t * turns * Math.PI * 2;
      const radius = 6 + 4 * Math.sin(t * Math.PI); // wider in middle
      // Two opposing helix strands per slice for that "tornado wrap" look.
      for (const sign of [-1, 1]) {
        const off = Math.sin(phase) * radius * sign;
        const lift = Math.cos(phase) * 1.6 * sign;
        const pxw = cx + px * off;
        const pyw = cy + py * off + lift;
        const swirl = this.scene.add.circle(pxw, pyw, 1.6 + Math.random() * 1.0,
          Math.random() < 0.4 ? 0xffffff : 0xffd84a, 1);
        this.scene.tweens.add({
          targets: swirl,
          x: pxw + Math.cos(ang) * 6,
          y: pyw + Math.sin(ang) * 6,
          alpha: 0,
          scale: 0.3,
          duration: 320 + Math.random() * 160,
          ease: "Quad.easeOut",
          onComplete: () => swirl.destroy(),
        });
        particles.push(swirl);
      }
    }

    // ---- Muzzle burst at caster ----
    const muzzle = this.scene.add.circle(sx, sy, 6, 0xffffff, 1);
    this.scene.tweens.add({
      targets: muzzle, scale: 2.6, alpha: 0,
      duration: 200, ease: "Quad.easeOut",
      onComplete: () => muzzle.destroy(),
    });
    const muzzleHalo = this.scene.add.circle(sx, sy, 10, 0xffd84a, 0.8);
    this.scene.tweens.add({
      targets: muzzleHalo, scale: 3, alpha: 0,
      duration: 280, ease: "Quad.easeOut",
      onComplete: () => muzzleHalo.destroy(),
    });

    // ---- Damage application (primary + splash) ----
    const dmg = Math.round(this.tower.damage);
    this.hit(this.target, dmg, tx, ty - 2, 0xffd84a, now);
    if (this.tower.splash > 0) {
      for (const e of this.enemies) {
        if (!e.alive || e === this.target) continue;
        const ep = e.position;
        if (Math.hypot(ep.x - tx, ep.y - ty) <= this.tower.splash) {
          const sd = Math.max(1, Math.round(dmg * 0.6));
          this.hit(e, sd, ep.x, ep.y - 2, 0xffd84a, now);
        }
      }
    }

    // ---- Impact tornado vortex ring at target (tightened) ----
    const vortex = this.scene.add.circle(tx, ty, 5, 0xffd84a, 0)
      .setStrokeStyle(2.4, 0xffd84a, 0.95);
    this.scene.tweens.add({
      targets: vortex, scale: 2.2, alpha: 0,
      duration: 340, ease: "Cubic.easeOut",
      onComplete: () => vortex.destroy(),
    });
    const vortex2 = this.scene.add.circle(tx, ty, 4, 0xffffff, 0)
      .setStrokeStyle(1.6, 0xffffff, 0.9);
    this.scene.tweens.add({
      targets: vortex2, scale: 2.8, alpha: 0,
      duration: 400, ease: "Cubic.easeOut",
      onComplete: () => vortex2.destroy(),
    });

    // ---- TSUNAMI WAVE FRONTS — three cresting moonwave arcs sweep outward
    //      from the impact (compact rings — visual tightness for premium look).
    for (let w = 0; w < 3; w++) {
      const delay = w * 120;
      const wave = this.scene.add.circle(tx, ty, 8, 0xc8d8ff, 0)
        .setStrokeStyle(3, 0xfff2a8, 0.95);
      const waveInner = this.scene.add.circle(tx, ty, 8, 0xfff2a8, 0)
        .setStrokeStyle(1.4, 0xffffff, 0.95);
      this.scene.tweens.add({
        targets: [wave, waveInner],
        scale: 2.4 - w * 0.3, alpha: 0,
        duration: 520, delay, ease: "Cubic.easeOut",
        onComplete: () => { wave.destroy(); waveInner.destroy(); },
      });
    }
    // Sweeping crest along the beam direction (tsunami push)
    const crest = this.scene.add.ellipse(tx, ty, 10, 30, 0xfff2a8, 0.85);
    crest.setRotation(ang + Math.PI / 2);
    this.scene.tweens.add({
      targets: crest,
      x: tx + Math.cos(ang) * 60,
      y: ty + Math.sin(ang) * 60,
      scaleX: 5.5, scaleY: 0.4, alpha: 0,
      duration: 520, ease: "Cubic.easeOut",
      onComplete: () => crest.destroy(),
    });
    // Foam droplet spray
    for (let i = 0; i < 10; i++) {
      const sa = (i / 10) * Math.PI * 2 + Math.random() * 0.4;
      const sd = 28 + Math.random() * 22;
      const drop = this.scene.add.circle(tx, ty, 1.6, Math.random() < 0.5 ? 0xffffff : 0xfff2a8, 1);
      this.scene.tweens.add({
        targets: drop,
        x: tx + Math.cos(sa) * sd,
        y: ty + Math.sin(sa) * sd,
        alpha: 0, scale: 0.4,
        duration: 480 + Math.random() * 200,
        ease: "Cubic.easeOut",
        onComplete: () => drop.destroy(),
      });
    }
    // 8 swirling debris specks at impact.
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + Math.random() * 0.5;
      const r0 = 4;
      const r1 = 18 + Math.random() * 10;
      const sp = this.scene.add.circle(tx + Math.cos(a) * r0, ty + Math.sin(a) * r0, 1.6,
        i % 2 === 0 ? 0xffffff : 0xffd84a, 1);
      this.scene.tweens.add({
        targets: sp,
        x: tx + Math.cos(a + 1.2) * r1,
        y: ty + Math.sin(a + 1.2) * r1,
        alpha: 0, scale: 0.3,
        duration: 380 + Math.random() * 160,
        ease: "Cubic.easeOut",
        onComplete: () => sp.destroy(),
      });
    }

    this.visual.destroy();
    this.alive = false;
    void particles; // particles self-destruct via tweens
  }

  /** Tinker plasma rocket — small homing rocket missile with bright thruster. */
  private buildRocketBarrage(): void {
    // HEAVY DEMOLITION BOMB — slow lobbed iron sphere with sizzling fuse.
    // Visual rotates with travel; we keep the sphere itself rotation-symmetric
    // and put the fuse on top so it visually arcs forward as the bomb travels.
    // Heat haze halo (fire glow tells the player "this is dangerous").
    const halo = this.scene.add.circle(0, 0, 12, 0xff5a1a, 0.35);
    // Outer iron shell (dark, slightly oblong egg-shape).
    const shell = this.scene.add.ellipse(0, 0, 16, 13, 0x1a1a20).setStrokeStyle(1, 0x000, 1);
    // Inner cast-iron tone for depth.
    const innerShell = this.scene.add.ellipse(0, 0, 13.5, 10.5, 0x2a2a32);
    // Top highlight.
    const shellHi = this.scene.add.ellipse(-1.5, -3, 5, 2, 0x6a6a78, 0.85);
    // Iron rivet ring around the bomb's equator.
    const rv1 = this.scene.add.circle(-5, 0, 0.8, 0x000, 1);
    const rv2 = this.scene.add.circle(5, 0, 0.8, 0x000, 1);
    const rv3 = this.scene.add.circle(0, -4.6, 0.8, 0x000, 1);
    const rv4 = this.scene.add.circle(0, 4.6, 0.8, 0x000, 1);
    // Brass fuse cap on top.
    const fuseCap = this.scene.add.rectangle(0, -6.5, 4, 2, 0xb08040).setStrokeStyle(0.5, 0x4a3010, 1);
    // Fuse string sticking up.
    const fuse = this.scene.add.rectangle(0, -9, 0.9, 4, 0x3a2a18);
    // BURNING SPARK at the fuse tip — intense flicker.
    const sparkGlow = this.scene.add.circle(0, -11, 3.6, 0xff8a3a, 0.85);
    const sparkCore = this.scene.add.circle(0, -11, 1.6, 0xffe070, 1);
    const sparkHot = this.scene.add.circle(0, -11.4, 0.7, 0xffffff, 1);
    // Skull warning mark on the bomb (premium / iconic).
    const skullEye1 = this.scene.add.circle(-1.6, -0.3, 0.6, 0xff5a1a, 0.95);
    const skullEye2 = this.scene.add.circle(1.6, -0.3, 0.6, 0xff5a1a, 0.95);
    const skullMouth = this.scene.add.rectangle(0, 1.6, 3, 0.6, 0x000, 1);

    // Slow heat-pulse on halo to telegraph weight.
    this.scene.tweens.add({
      targets: halo,
      scale: { from: 0.9, to: 1.25 }, alpha: { from: 0.25, to: 0.55 },
      yoyo: true, repeat: -1, duration: 280,
    });
    // Manic fuse sparkle.
    this.scene.tweens.add({
      targets: [sparkGlow, sparkCore, sparkHot],
      scale: { from: 0.7, to: 1.4 }, alpha: { from: 0.7, to: 1 },
      yoyo: true, repeat: -1, duration: 70,
    });
    // Glowing skull eyes flicker.
    this.scene.tweens.add({
      targets: [skullEye1, skullEye2],
      alpha: { from: 0.5, to: 1 },
      yoyo: true, repeat: -1, duration: 220,
    });
    // Bomb gently tumbles — slight rotation tween on a child wrapper would be
    // ideal but the visual rotates to face motion, so add a subtle wobble
    // by tweening the shell ellipse scale slightly (heavy-bomb wobble).
    this.scene.tweens.add({
      targets: [shell, innerShell],
      scaleX: { from: 0.96, to: 1.04 }, scaleY: { from: 1.04, to: 0.96 },
      yoyo: true, repeat: -1, duration: 380, ease: "Sine.easeInOut",
    });

    this.visual.add([
      halo,
      shell, innerShell, shellHi,
      skullEye1, skullEye2, skullMouth,
      rv1, rv2, rv3, rv4,
      fuseCap, fuse,
      sparkGlow, sparkCore, sparkHot,
    ]);
  }

  private buildArrow(): void {
    // Moon-glow halo (carries element identity)
    const halo = this.scene.add.circle(0, 0, 8, this.tower.color, 0.3);
    this.scene.tweens.add({
      targets: halo,
      scale: { from: 0.8, to: 1.18 },
      yoyo: true,
      repeat: -1,
      duration: 240,
    });
    // Silver shaft (rectangle along +x since visual rotates to face motion)
    const shaft = this.scene.add.rectangle(0, 0, 18, 1.6, 0xe8eef7, 1);
    // Two fletching feathers (element-tinted) at the tail
    const fletchTop = this.scene.add.triangle(-8, -1.6, 0, 0, -5, -2.4, 0, -3.2, this.tower.color);
    const fletchBot = this.scene.add.triangle(-8,  1.6, 0, 0, -5,  2.4, 0,  3.2, this.tower.color);
    // Steel arrowhead at the tip
    const head = this.scene.add
      .triangle(9, 0, 0, -3, 7, 0, 0, 3, 0xc9d4e8)
      .setStrokeStyle(0.6, 0xffffff, 1);
    this.visual.add([halo, shaft, fletchTop, fletchBot, head]);
  }

  private buildEnergyBolt(): void {
    // Crackling energy bolt — small bright core with element-colored glow.
    const glow = this.scene.add.circle(0, 0, 7, this.tower.color, 0.55);
    const core = this.scene.add.circle(0, 0, 3.4, 0xffffff).setStrokeStyle(1, this.tower.color, 1);
    // Trailing zigzag spark line
    const tail = this.scene.add.rectangle(-6, 0, 8, 1.2, this.tower.color, 0.9);
    this.scene.tweens.add({
      targets: glow,
      scale: { from: 0.85, to: 1.2 },
      yoyo: true,
      repeat: -1,
      duration: 160,
    });
    this.visual.add([tail, glow, core]);
  }

  private buildBullet(): void {
    // Tracer streak — bright core with motion-blur tail.
    const trail = this.scene.add.rectangle(-5, 0, 10, 1.4, this.tower.color, 0.85);
    const core = this.scene.add.circle(0, 0, 2.2, 0xffffff).setStrokeStyle(1, this.tower.color, 1);
    this.visual.add([trail, core]);
  }

  private buildOrb(): void {
    // Magic orb with pulsing glow.
    const glow = this.scene.add.circle(0, 0, 9, this.tower.color, 0.45);
    const core = this.scene.add.circle(0, 0, 4, 0xffffff).setStrokeStyle(2, this.tower.color, 1);
    this.scene.tweens.add({
      targets: glow,
      scale: { from: 0.85, to: 1.15 },
      yoyo: true,
      repeat: -1,
      duration: 220,
    });
    this.visual.add([glow, core]);
  }

  update(deltaMs: number, now: number): boolean {
    if (!this.alive) return false;

    if (!this.target.alive) {
      this.destroy();
      return false;
    }

    // ── Boush bomb drop: parabolic arc + ground shadow ────────────────
    // Tracks a separate "ground position" advancing toward the target,
    // while the visual lifts above it on a sine arc and falls into impact.
    if (this.visualKind === "rocket_barrage") {
      const tx0 = this.target.position.x;
      const ty0 = this.target.position.y;
      const gdx = tx0 - this.bombGroundX;
      const gdy = ty0 - this.bombGroundY;
      const gdist = Math.hypot(gdx, gdy);
      const step = (this.speed * deltaMs) / 1000;

      if (gdist <= step + this.target.def.radius * 0.6) {
        this.applyHit(now);
        this.destroy();
        return false;
      }

      this.bombGroundX += (gdx / gdist) * step;
      this.bombGroundY += (gdy / gdist) * step;
      this.bombTraveled += step;

      const prog = Math.min(1, this.bombTraveled / this.bombInitDist);
      // Sine-curve arc — peak height scales with throw distance, capped.
      const arcH = Math.sin(prog * Math.PI) * Math.min(70, this.bombInitDist * 0.45);

      this.visual.setPosition(this.bombGroundX, this.bombGroundY - arcH);
      // Tumble: bomb spins as it flies (heavy, slow rotation).
      this.bombTumble += deltaMs * 0.0035;
      this.visual.setRotation(this.bombTumble);

      // Shadow grows + darkens as bomb nears ground (prog → 1).
      if (this.bombShadow) {
        const sScale = 0.5 + prog * 0.7;
        this.bombShadow.setPosition(this.bombGroundX, this.bombGroundY + 1);
        this.bombShadow.setScale(sScale, sScale);
        this.bombShadow.setAlpha(0.25 + prog * 0.45);
      }

      // Trail (sparse fuse smoke).
      this.trailAccum += deltaMs;
      if (this.trailAccum >= 4) {
        this.trailAccum = 0;
        this.spawnTrailParticle();
      }
      return true;
    }

    const tx = this.target.position.x;
    const ty = this.target.position.y;
    const dx = tx - this.visual.x;
    const dy = ty - this.visual.y;
    const dist = Math.hypot(dx, dy);
    const step = (this.speed * deltaMs) / 1000;

    if (dist <= step + this.target.def.radius * 0.6) {
      this.applyHit(now);
      this.destroy();
      return false;
    }

    const nx = this.visual.x + (dx / dist) * step;
    const ny = this.visual.y + (dy / dist) * step;
    this.visual.setPosition(nx, ny);
    // Rotate visual to face direction of motion — skip for symmetric/spinning shapes
    // (hammer, axe, soul wisp, beast axes, snowflake have their own spin).
    if (
      this.visualKind !== "storm_hammer" &&
      this.visualKind !== "bone_axe" &&
      this.visualKind !== "soul_wisp" &&
      this.visualKind !== "beast_axes" &&
      this.visualKind !== "snowflake" &&
      this.visualKind !== "death_scythe" &&
      this.visualKind !== "phantom_blade" &&
      this.visualKind !== "void_blade"
    ) {
      this.visual.setRotation(Math.atan2(dy, dx));
    }

    // Trail (lighter for energy bolts, heavier for orbs).
    this.trailAccum += deltaMs;
    const trailFreq =
      this.visualKind === "arrow"
        ? 22
        : this.visualKind === "flame"
        ? 14
        : this.visualKind === "tidal_wave"
        ? 16
        : this.visualKind === "boulder_rocket"
        ? 14
        : this.visualKind === "lightning"
        ? 12
        : this.visualKind === "dual_breath"
        ? 12
        : this.visualKind === "storm_hammer"
        ? 14
        : this.visualKind === "bone_axe"
        ? 24
        : this.visualKind === "snowflake"
        ? 18
        : this.visualKind === "vine_whip"
        ? 22
        : this.visualKind === "brew_jug"
        ? 14
        : this.visualKind === "soul_wisp"
        ? 22
        : this.visualKind === "beast_axes"
        ? 26
        : this.visualKind === "tentacle"
        ? 18
        : this.visualKind === "death_scythe"
        ? 7
        : this.visualKind === "phantom_blade"
        ? 5
        : this.visualKind === "void_blade"
        ? 6
        : this.archetype === "gunner"
        ? 18
        : this.archetype === "handcaster"
        ? 25
        : 30;
    if (this.trailAccum >= trailFreq) {
      this.trailAccum = 0;
      this.spawnTrailParticle();
    }
    return true;
  }

  private spawnTrailParticle(): void {
    const jitterX = (Math.random() - 0.5) * 4;
    const jitterY = (Math.random() - 0.5) * 4;

    // Mirana arrow: bright-white moondust core + faint element-tinted halo.
    if (this.visualKind === "arrow") {
      const core = this.scene.add.circle(
        this.visual.x + jitterX,
        this.visual.y + jitterY,
        1.1,
        0xffffff,
        1,
      );
      const halo = this.scene.add.circle(
        this.visual.x + jitterX,
        this.visual.y + jitterY,
        2.4,
        this.tower.color,
        0.55,
      );
      this.scene.tweens.add({
        targets: [core, halo],
        alpha: 0,
        scale: 0.2,
        duration: 280,
        ease: "Quad.easeOut",
        onComplete: () => {
          core.destroy();
          halo.destroy();
        },
      });
      return;
    }

    // Lina flame: chunky red/orange ember that drifts sideways as it fades.
    if (this.visualKind === "flame") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      const ember = this.scene.add.circle(px, py, 2.6 + Math.random() * 1.6, 0xff5a3a, 0.95);
      const driftX = (Math.random() - 0.5) * 8;
      const driftY = -2 - Math.random() * 4; // smoke rises a little
      this.scene.tweens.add({
        targets: ember,
        x: px + driftX,
        y: py + driftY,
        alpha: 0,
        scale: 0.3,
        duration: 220,
        ease: "Quad.easeOut",
        onComplete: () => ember.destroy(),
      });
      // Yellow-white spark core
      const spark = this.scene.add.circle(px, py, 1, 0xfff2a8, 1);
      this.scene.tweens.add({
        targets: spark,
        alpha: 0,
        scale: 0.2,
        duration: 140,
        onComplete: () => spark.destroy(),
      });
      return;
    }

    // Tidal wave trail: white sea foam droplets that spread and fade.
    if (this.visualKind === "tidal_wave") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      const foam = this.scene.add.circle(px, py, 2.4 + Math.random() * 1.2, 0xffffff, 0.85);
      const halo = this.scene.add.circle(px, py, 5, 0x2a8acf, 0.6);
      this.scene.tweens.add({
        targets: [foam, halo],
        alpha: 0,
        scale: 1.6,
        duration: 360,
        ease: "Quad.easeOut",
        onComplete: () => { foam.destroy(); halo.destroy(); },
      });
      return;
    }

    // Zeus lightning: sharp white-blue electric sparks that fork outward briefly.
    if (this.visualKind === "lightning") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      // Main spark — white-yellow flash dot.
      const spark = this.scene.add.circle(px, py, 2.2 + Math.random() * 1.2, 0xfff5c0, 1);
      // Outer electric-blue halo.
      const ring = this.scene.add.circle(px, py, 4.5, 0x88ccff, 0.55);
      this.scene.tweens.add({
        targets: [spark, ring],
        alpha: 0,
        scale: 0.2,
        duration: 130,
        ease: "Quad.easeOut",
        onComplete: () => { spark.destroy(); ring.destroy(); },
      });
      return;
    }

    // Boulder rocket: smoke + spark trail that lingers.
    if (this.visualKind === "boulder_rocket") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      const smoke = this.scene.add.circle(px, py, 3 + Math.random() * 2, 0x6a5040, 0.65);
      const spark = this.scene.add.circle(px, py, 1.2, 0xff8a3a, 1);
      this.scene.tweens.add({
        targets: smoke,
        alpha: 0,
        scale: 1.8,
        duration: 460,
        ease: "Quad.easeOut",
        onComplete: () => smoke.destroy(),
      });
      this.scene.tweens.add({
        targets: spark,
        alpha: 0,
        scale: 0.2,
        duration: 200,
        onComplete: () => spark.destroy(),
      });
      return;
    }

    // Dual breath: alternating blue ice flecks and red fire embers, drifting apart vertically.
    if (this.visualKind === "dual_breath") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      // Blue ice fleck (drifts up).
      const ice = this.scene.add.circle(px, py - 2, 2 + Math.random() * 1.2, 0xa6e3ff, 0.9);
      const iceCore = this.scene.add.circle(px, py - 2, 0.8, 0xffffff, 1);
      this.scene.tweens.add({
        targets: [ice, iceCore],
        y: py - 8 - Math.random() * 4,
        alpha: 0,
        scale: 0.3,
        duration: 360,
        ease: "Quad.easeOut",
        onComplete: () => { ice.destroy(); iceCore.destroy(); },
      });
      // Red fire ember (drifts down).
      const fire = this.scene.add.circle(px, py + 2, 2.2 + Math.random() * 1.2, 0xff7a3a, 0.95);
      const fireCore = this.scene.add.circle(px, py + 2, 0.9, 0xfff2a8, 1);
      this.scene.tweens.add({
        targets: [fire, fireCore],
        y: py + 8 + Math.random() * 4,
        alpha: 0,
        scale: 0.3,
        duration: 320,
        ease: "Quad.easeOut",
        onComplete: () => { fire.destroy(); fireCore.destroy(); },
      });
      return;
    }

    // Storm hammer: bright purple/white electric sparks lingering along path.
    if (this.visualKind === "storm_hammer") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      const spark = this.scene.add.circle(px, py, 1.2 + Math.random() * 0.8,
        Math.random() < 0.4 ? 0xffffff : 0xd080ff, 1);
      const halo = this.scene.add.circle(px, py, 3.2, 0xb84aff, 0.55);
      this.scene.tweens.add({
        targets: [spark, halo],
        alpha: 0,
        scale: 0.2,
        duration: 240,
        ease: "Quad.easeOut",
        onComplete: () => { spark.destroy(); halo.destroy(); },
      });
      return;
    }

    // Bone axe: fat brown drip splatter dots and bone splinter flecks.
    if (this.visualKind === "bone_axe") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      const splat = this.scene.add.ellipse(px, py, 3.5 + Math.random() * 2, 2.5, 0x4a3520, 0.85);
      const fleck = this.scene.add.rectangle(px, py, 1.4, 1, 0xeae0c4, 0.9).setRotation(Math.random() * Math.PI);
      this.scene.tweens.add({
        targets: splat,
        y: py + 6 + Math.random() * 4,
        alpha: 0,
        scaleY: 0.4,
        duration: 540,
        ease: "Cubic.easeIn",
        onComplete: () => splat.destroy(),
      });
      this.scene.tweens.add({
        targets: fleck,
        alpha: 0,
        scale: 0.2,
        duration: 320,
        onComplete: () => fleck.destroy(),
      });
      return;
    }

    // Snowflake shuriken: tiny crystalline frost flecks that twinkle.
    if (this.visualKind === "snowflake") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      const flake = this.scene.add.rectangle(px, py, 1.6, 1.6, 0xffffff, 1).setRotation(Math.random() * Math.PI);
      const halo = this.scene.add.circle(px, py, 2.4, 0xa6e3ff, 0.55);
      this.scene.tweens.add({
        targets: [flake, halo],
        alpha: 0,
        scale: 0.2,
        duration: 360,
        ease: "Quad.easeOut",
        onComplete: () => { flake.destroy(); halo.destroy(); },
      });
      return;
    }

    // Vine whip: lingering toxic-green mist with bright sickly spark + leaf flecks.
    if (this.visualKind === "vine_whip") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      const mist = this.scene.add.circle(px, py, 3.2 + Math.random() * 1.4, 0x4ea84a, 0.6);
      const spark = this.scene.add.circle(px, py, 1, 0xd4ff70, 1);
      this.scene.tweens.add({
        targets: mist,
        alpha: 0,
        scale: 1.7,
        duration: 540,
        ease: "Quad.easeOut",
        onComplete: () => mist.destroy(),
      });
      this.scene.tweens.add({
        targets: spark,
        alpha: 0,
        scale: 0.2,
        duration: 200,
        onComplete: () => spark.destroy(),
      });
      return;
    }

    // Brewmaster brew jug: orange ember + smoke trail.
    if (this.visualKind === "brew_jug") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      const ember = this.scene.add.circle(px, py, 2.4 + Math.random() * 1.4, 0xff8a3a, 0.95);
      const smoke = this.scene.add.circle(px, py, 3, 0x6a5040, 0.55);
      this.scene.tweens.add({
        targets: ember,
        x: px + (Math.random() - 0.5) * 6,
        y: py - 2 - Math.random() * 4,
        alpha: 0,
        scale: 0.3,
        duration: 240,
        ease: "Quad.easeOut",
        onComplete: () => ember.destroy(),
      });
      this.scene.tweens.add({
        targets: smoke,
        alpha: 0,
        scale: 1.6,
        duration: 460,
        onComplete: () => smoke.destroy(),
      });
      return;
    }

    // Pugna soul wisp: green/purple soul wisp particle that drifts.
    if (this.visualKind === "soul_wisp") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      const soul = this.scene.add.circle(px, py, 1.6 + Math.random() * 0.9,
        Math.random() < 0.5 ? 0x6ee0a0 : 0xb84aff, 0.95);
      const halo = this.scene.add.circle(px, py, 3.5, 0xeaf8e8, 0.55);
      this.scene.tweens.add({
        targets: [soul, halo],
        alpha: 0,
        scale: 0.3,
        y: py - 3 - Math.random() * 3,
        duration: 360,
        ease: "Quad.easeOut",
        onComplete: () => { soul.destroy(); halo.destroy(); },
      });
      return;
    }

    // Beastmaster wild axes: blood-mist droplets and small steel sparks.
    if (this.visualKind === "beast_axes") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      const blood = this.scene.add.circle(px, py, 1.8 + Math.random() * 1.2, 0x8a2818, 0.85);
      const steel = this.scene.add.rectangle(px, py, 1.6, 0.8, 0xc8c8c8, 1).setRotation(Math.random() * Math.PI);
      this.scene.tweens.add({
        targets: blood,
        y: py + 4 + Math.random() * 4,
        alpha: 0,
        scale: 0.4,
        duration: 420,
        ease: "Cubic.easeIn",
        onComplete: () => blood.destroy(),
      });
      this.scene.tweens.add({
        targets: steel,
        alpha: 0,
        scale: 0.2,
        duration: 220,
        onComplete: () => steel.destroy(),
      });
      return;
    }

    // Necrolyte death scythe — DEATH WAVE TSUNAMI:
    // wide necrotic green wake of soul ribbons + skull motes + dark wave arc.
    if (this.visualKind === "death_scythe") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      // Wide wave ribbon — perpendicular crescent of decay
      const ang = this.visual.rotation;
      const perpAng = ang + Math.PI / 2;
      const wave = this.scene.add.ellipse(px, py, 22, 5, 0x2a4a3a, 0.85).setStrokeStyle(0.8, 0x6aff8a, 0.95);
      wave.setRotation(perpAng);
      this.scene.tweens.add({
        targets: wave,
        scaleX: 1.8, scaleY: 0.4, alpha: 0,
        duration: 480, ease: "Quad.easeOut",
        onComplete: () => wave.destroy(),
      });
      // Bright soul wisps (3 per spawn — denser tsunami wake)
      for (let i = 0; i < 3; i++) {
        const ox = (Math.random() - 0.5) * 14;
        const oy = (Math.random() - 0.5) * 8;
        const wisp = this.scene.add.circle(px + ox, py + oy, 2.4 + Math.random() * 1.6, 0x6aff8a, 0.95);
        const core = this.scene.add.circle(px + ox, py + oy, 1.1, 0xffffff, 1);
        this.scene.tweens.add({
          targets: [wisp, core],
          x: px + ox + (Math.random() - 0.5) * 10,
          y: py + oy - 6 - Math.random() * 8,
          alpha: 0,
          scale: 0.3,
          duration: 460 + Math.random() * 160,
          ease: "Quad.easeOut",
          onComplete: () => { wisp.destroy(); core.destroy(); },
        });
      }
      // Tiny skull mote drifting up
      if (Math.random() < 0.5) {
        const sk = this.scene.add.circle(px + (Math.random() - 0.5) * 8, py, 1.4, 0xeae0c0, 1).setStrokeStyle(0.4, 0x000, 1);
        this.scene.tweens.add({
          targets: sk, y: py - 14, alpha: 0, scale: 0.4,
          duration: 600, ease: "Quad.easeOut",
          onComplete: () => sk.destroy(),
        });
      }
      return;
    }

    // Phantom blade — BLADE STORM AFTERIMAGE: trio of parallel violet streaks
    // give the impression of multiple ghost blades streaming alongside the cut.
    if (this.visualKind === "phantom_blade") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      const ang = this.visual.rotation;
      const perpX = -Math.sin(ang);
      const perpY = Math.cos(ang);
      // Three afterimages at perpendicular offsets (-3, 0, +3) for storm-feel.
      for (const off of [-3.5, 0, 3.5]) {
        const streak = this.scene.add.rectangle(
          px + perpX * off,
          py + perpY * off,
          18, 1.6,
          off === 0 ? 0xffffff : 0xd4a0ff,
          off === 0 ? 1 : 0.85,
        );
        streak.setRotation(ang);
        this.scene.tweens.add({
          targets: streak,
          alpha: 0, scaleX: 0.2,
          duration: 220, ease: "Quad.easeOut",
          onComplete: () => streak.destroy(),
        });
      }
      // Sparkle motes drifting back
      const sparkle = this.scene.add.circle(px, py, 1.4, 0xffffff, 1);
      this.scene.tweens.add({
        targets: sparkle, alpha: 0, scale: 0.2,
        duration: 200, onComplete: () => sparkle.destroy(),
      });
      // Occasional shadow petal
      if (Math.random() < 0.4) {
        const petal = this.scene.add.triangle(px, py, 0, 0, -2, 5, 2, 5, 0x6a3a8a, 0.85);
        petal.setRotation(ang + Math.PI);
        this.scene.tweens.add({
          targets: petal, alpha: 0, scale: 0.4,
          duration: 320, onComplete: () => petal.destroy(),
        });
      }
      return;
    }

    // Void blade — twin-fan buzzsaw wake: a bright violet ring + crackle motes
    // pulsing off the spinning disc (distinct from the phantom's streak storm).
    if (this.visualKind === "void_blade") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      const ring = this.scene.add.circle(px, py, 5.5, 0xb84aff, 0.4).setStrokeStyle(1.2, 0xd4a0ff, 0.8);
      this.scene.tweens.add({
        targets: ring, alpha: 0, scale: 0.2,
        duration: 230, ease: "Quad.easeOut",
        onComplete: () => ring.destroy(),
      });
      const mote = this.scene.add.circle(px, py, 1.3 + Math.random() * 0.8,
        Math.random() < 0.4 ? 0xffffff : 0xd080ff, 1);
      this.scene.tweens.add({
        targets: mote, alpha: 0, scale: 0.2,
        duration: 200, onComplete: () => mote.destroy(),
      });
      return;
    }

    // Tinker BOMB — drifting fuse-smoke + ember sparks behind the lobbed bomb.
    if (this.visualKind === "rocket_barrage") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y - 10 + jitterY; // smoke trails from fuse on top
      // Heavy gray smoke puff that lingers and rises
      const smoke = this.scene.add.circle(px, py, 3.4 + Math.random() * 1.6, 0x4a3a30, 0.7);
      this.scene.tweens.add({
        targets: smoke,
        x: px + (Math.random() - 0.5) * 10,
        y: py - 10 - Math.random() * 6,
        alpha: 0, scale: 2.4,
        duration: 700 + Math.random() * 200,
        ease: "Quad.easeOut",
        onComplete: () => smoke.destroy(),
      });
      // Bright orange ember
      const ember = this.scene.add.circle(px, py, 1.4 + Math.random() * 0.9, 0xff8a3a, 1);
      const emberCore = this.scene.add.circle(px, py, 0.6, 0xffe070, 1);
      this.scene.tweens.add({
        targets: [ember, emberCore],
        x: px + (Math.random() - 0.5) * 6,
        y: py - 4 - Math.random() * 4,
        alpha: 0, scale: 0.3,
        duration: 380, ease: "Quad.easeOut",
        onComplete: () => { ember.destroy(); emberCore.destroy(); },
      });
      return;
    }

    // Tidehunter tentacle: water splash + foam droplets behind.
    if (this.visualKind === "tentacle") {
      const px = this.visual.x + jitterX;
      const py = this.visual.y + jitterY;
      const drop = this.scene.add.circle(px, py, 1.8 + Math.random() * 1.1, 0x6ad8ff, 0.95);
      const foam = this.scene.add.circle(px, py, 3, 0xffffff, 0.55);
      this.scene.tweens.add({
        targets: drop,
        y: py + 5 + Math.random() * 4,
        alpha: 0,
        scale: 0.3,
        duration: 460,
        ease: "Cubic.easeIn",
        onComplete: () => drop.destroy(),
      });
      this.scene.tweens.add({
        targets: foam,
        alpha: 0,
        scale: 1.6,
        duration: 360,
        onComplete: () => foam.destroy(),
      });
      return;
    }

    const size = this.archetype === "gunner" ? 1.2 : 2 + Math.random() * 1.5;
    const p = this.scene.add.circle(this.visual.x + jitterX, this.visual.y + jitterY, size, this.tower.color, 0.85);
    this.scene.tweens.add({
      targets: p,
      alpha: 0,
      scale: 0.2,
      duration: this.archetype === "gunner" ? 180 : 320,
      ease: "Quad.easeOut",
      onComplete: () => p.destroy(),
    });
  }

  private applyHit(now: number): void {
    const hits: Enemy[] = [];
    if (this.tower.splash > 0) {
      const tx = this.target.position.x;
      const ty = this.target.position.y;
      for (const e of this.enemies) {
        if (!e.alive) continue;
        const ep = e.position;
        if (Math.hypot(ep.x - tx, ep.y - ty) <= this.tower.splash) hits.push(e);
      }
    } else {
      hits.push(this.target);
    }

    for (const e of hits) {
      const ep = e.position;
      this.hit(e, this.tower.damage, ep.x, ep.y - 2, this.tower.color, now);
    }

    // Element-themed impact burst — Dota-style spell effect.
    const ix = this.target.position.x;
    const iy = this.target.position.y;
    const color = this.tower.color;

    // Universal anime impact pop — a quick white flash core + element shock ring
    // on EVERY hit so each shot landing feels punchy (not just crits).
    const popCore = this.scene.add.circle(ix, iy, 7, 0xffffff, 0.9).setDepth(95);
    this.scene.tweens.add({
      targets: popCore,
      scale: { from: 0.5, to: 1.8 },
      alpha: 0,
      duration: 150,
      ease: "Quad.easeOut",
      onComplete: () => popCore.destroy(),
    });
    const popRing = this.scene.add.circle(ix, iy, 4, color, 0).setStrokeStyle(2, color, 0.9).setDepth(95);
    this.scene.tweens.add({
      targets: popRing,
      scale: { from: 0.6, to: 3.2 },
      alpha: { from: 0.9, to: 0 },
      duration: 240,
      ease: "Cubic.easeOut",
      onComplete: () => popRing.destroy(),
    });

    // Splash ring (only for AOE towers)
    if (this.tower.splash > 0) {
      const ring = this.scene.add.circle(ix, iy, 6, color, 0.4).setStrokeStyle(2, color, 0.85);
      this.scene.tweens.add({
        targets: ring,
        radius: this.tower.splash,
        alpha: 0,
        duration: 320,
        ease: "Cubic.easeOut",
        onComplete: () => ring.destroy(),
      });
    }

    // Zeus lightning: replace standard impact with a vertical electric strike.
    if (this.visualKind === "lightning") {
      // Bright white strike flash.
      const strike = this.scene.add.circle(ix, iy, 16, 0xffffff, 1);
      this.scene.tweens.add({
        targets: strike,
        scale: 0.1,
        alpha: 0,
        duration: 160,
        onComplete: () => strike.destroy(),
      });
      // Electric-blue expanding ring.
      const eRing = this.scene.add.circle(ix, iy, 8, 0x88ccff, 0.7).setStrokeStyle(2, 0xffffff, 0.9);
      this.scene.tweens.add({
        targets: eRing,
        scale: 3.5,
        alpha: 0,
        duration: 280,
        ease: "Cubic.easeOut",
        onComplete: () => eRing.destroy(),
      });
      // Golden bolt shards — 8 radial streaks.
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const len = 16 + Math.random() * 14;
        const shard = this.scene.add.rectangle(ix, iy, len, 1.5, 0xffd84a, 1);
        shard.setRotation(angle);
        this.scene.tweens.add({
          targets: shard,
          x: ix + Math.cos(angle) * len * 0.5,
          y: iy + Math.sin(angle) * len * 0.5,
          alpha: 0,
          duration: 220 + Math.random() * 100,
          ease: "Quad.easeOut",
          onComplete: () => shard.destroy(),
        });
      }
      return;
    }

    // Boulder rocket: compact fiery explosion — orange/yellow fireball, dark
    // smoke ring, white flash core, radial debris shards. Tightened radius.
    if (this.visualKind === "boulder_rocket") {
      // Inner white-hot core flash.
      const coreFlash = this.scene.add.circle(ix, iy, 10, 0xffffff, 1);
      this.scene.tweens.add({
        targets: coreFlash,
        scale: 0.2,
        alpha: 0,
        duration: 160,
        ease: "Quad.easeOut",
        onComplete: () => coreFlash.destroy(),
      });
      // Yellow fireball mid-layer.
      const fireball = this.scene.add.circle(ix, iy, 9, 0xffd84a, 0.95);
      this.scene.tweens.add({
        targets: fireball,
        scale: 1.6,
        alpha: 0,
        duration: 280,
        ease: "Quad.easeOut",
        onComplete: () => fireball.destroy(),
      });
      // Orange outer fire ring.
      const orange = this.scene.add.circle(ix, iy, 11, 0xff7a1a, 0.85);
      this.scene.tweens.add({
        targets: orange,
        scale: 1.9,
        alpha: 0,
        duration: 360,
        ease: "Quad.easeOut",
        onComplete: () => orange.destroy(),
      });
      // Dark smoke ring expanding outward (charcoal).
      const smokeRing = this.scene.add.circle(ix, iy, 7, 0x1a1a1a, 0).setStrokeStyle(2.5, 0x2a2a2a, 0.85);
      this.scene.tweens.add({
        targets: smokeRing,
        scale: 2.4,
        alpha: 0,
        duration: 460,
        ease: "Cubic.easeOut",
        onComplete: () => smokeRing.destroy(),
      });
      // Drifting smoke puffs.
      for (let i = 0; i < 4; i++) {
        const ang = Math.random() * Math.PI * 2;
        const r = 4 + Math.random() * 6;
        const sx = ix + Math.cos(ang) * r;
        const sy = iy + Math.sin(ang) * r;
        const puff = this.scene.add.circle(sx, sy, 3 + Math.random() * 2, 0x3a3030, 0.85);
        this.scene.tweens.add({
          targets: puff,
          x: sx + Math.cos(ang) * 10,
          y: sy + Math.sin(ang) * 10 - 4,
          alpha: 0,
          scale: 1.4,
          duration: 600 + Math.random() * 180,
          ease: "Quad.easeOut",
          onComplete: () => puff.destroy(),
        });
      }
      // Bright fire embers — radial sparks (yellow + orange mix).
      const emberCount = 9;
      for (let i = 0; i < emberCount; i++) {
        const angle = (i / emberCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
        const dist = 13 + Math.random() * 14;
        const isHot = Math.random() < 0.5;
        const ember = this.scene.add.circle(ix, iy, 1.2 + Math.random() * 0.9,
          isHot ? 0xfff2a8 : 0xff7a1a, 1);
        this.scene.tweens.add({
          targets: ember,
          x: ix + Math.cos(angle) * dist,
          y: iy + Math.sin(angle) * dist,
          alpha: 0,
          scale: 0.2,
          duration: 380 + Math.random() * 160,
          ease: "Cubic.easeOut",
          onComplete: () => ember.destroy(),
        });
      }
      // Stone debris shards — fly outward, rotating.
      for (let i = 0; i < 4; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = 11 + Math.random() * 10;
        const shard = this.scene.add.triangle(ix, iy, 0, -2, -2, 2, 2, 2, 0x6a4a28)
          .setStrokeStyle(0.7, 0x2a1a08, 1);
        shard.setRotation(Math.random() * Math.PI * 2);
        this.scene.tweens.add({
          targets: shard,
          x: ix + Math.cos(angle) * dist,
          y: iy + Math.sin(angle) * dist + 4, // gravity arc
          angle: shard.angle + (Math.random() - 0.5) * 540,
          alpha: 0,
          duration: 500 + Math.random() * 180,
          ease: "Quad.easeIn",
          onComplete: () => shard.destroy(),
        });
      }
      return;
    }

    // Necrolyte death scythe — DEATH WAVE TSUNAMI explosion: layered rings,
    // wide green wave wash, and rising skull spirits.
    if (this.visualKind === "death_scythe") {
      const flash = this.scene.add.circle(ix, iy, 16, 0xffffff, 1);
      this.scene.tweens.add({ targets: flash, scale: 0.2, alpha: 0, duration: 200, onComplete: () => flash.destroy() });
      // Three expanding rings staggered for tsunami feel
      for (let r = 0; r < 3; r++) {
        const ring = this.scene.add.circle(ix, iy, 8, 0x6aff8a, 0).setStrokeStyle(2.6 - r * 0.4, 0x6aff8a, 0.95);
        this.scene.tweens.add({
          targets: ring, scale: 4.2 - r * 0.5, alpha: 0,
          duration: 540 + r * 60, delay: r * 90, ease: "Cubic.easeOut",
          onComplete: () => ring.destroy(),
        });
      }
      // Wide death wave wash (oblong)
      const wash = this.scene.add.ellipse(ix, iy, 60, 18, 0x2a4a3a, 0.7).setStrokeStyle(1.2, 0x6aff8a, 0.9);
      this.scene.tweens.add({
        targets: wash, scaleX: 1.6, alpha: 0,
        duration: 520, ease: "Cubic.easeOut",
        onComplete: () => wash.destroy(),
      });
      // Inner emerald burst.
      const core = this.scene.add.circle(ix, iy, 10, 0x6aff8a, 0.9);
      this.scene.tweens.add({ targets: core, scale: 2.4, alpha: 0, duration: 320, ease: "Quad.easeOut", onComplete: () => core.destroy() });
      // 6 floating skull motes drifting up.
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const sk = this.scene.add.circle(ix, iy, 1.8, 0xeae0c0, 1).setStrokeStyle(0.5, 0x000, 1);
        const eye = this.scene.add.circle(ix, iy, 0.6, 0x6aff8a, 1);
        this.scene.tweens.add({
          targets: [sk, eye],
          x: ix + Math.cos(a) * 18,
          y: iy + Math.sin(a) * 18 - 6,
          alpha: 0, scale: 0.3,
          duration: 520 + Math.random() * 160,
          ease: "Quad.easeOut",
          onComplete: () => { sk.destroy(); eye.destroy(); },
        });
      }
      return;
    }

    // Phantom Assassin blade — BLADE STORM CLEAVE: 4 crossed slashes for the
    // blade-storm afterimage feel, plus a wide violet cleave streak.
    if (this.visualKind === "phantom_blade") {
      // SIX cross-cut slash lines through the impact point — bigger storm.
      const slashAngs = [-1.0, -0.5, 0, 0.5, 1.0, 1.5];
      for (const slashAng of slashAngs) {
        const slash = this.scene.add.rectangle(ix, iy, 72, 3.6, 0xffffff, 1).setStrokeStyle(0.6, 0xd4a0ff, 1);
        slash.setRotation(slashAng);
        this.scene.tweens.add({
          targets: slash, scaleX: 2.0, alpha: 0,
          duration: 360, ease: "Quad.easeOut",
          onComplete: () => slash.destroy(),
        });
      }
      // Wide horizontal cleave streak — much wider.
      const cleave = this.scene.add.rectangle(ix, iy, 110, 6, 0xd4a0ff, 0.9);
      cleave.setRotation(this.visual.rotation);
      this.scene.tweens.add({
        targets: cleave, scaleX: 1.6, scaleY: 0.2, alpha: 0,
        duration: 420, ease: "Cubic.easeOut",
        onComplete: () => cleave.destroy(),
      });
      // Violet shockwave ring — bigger.
      const ring = this.scene.add.circle(ix, iy, 10, 0xd4a0ff, 0).setStrokeStyle(3.2, 0xd4a0ff, 0.95);
      this.scene.tweens.add({ targets: ring, scale: 4.5, alpha: 0, duration: 440, ease: "Cubic.easeOut", onComplete: () => ring.destroy() });
      // Second outer shockwave for depth.
      const ring2 = this.scene.add.circle(ix, iy, 14, 0x8a4ac8, 0).setStrokeStyle(2.2, 0xd4a0ff, 0.75);
      this.scene.tweens.add({ targets: ring2, scale: 5.5, alpha: 0, duration: 540, ease: "Cubic.easeOut", onComplete: () => ring2.destroy() });
      // Bright crit flash — bigger.
      const crit = this.scene.add.circle(ix, iy, 22, 0xffffff, 1);
      this.scene.tweens.add({ targets: crit, scale: 0.3, alpha: 0, duration: 260, ease: "Quad.easeOut", onComplete: () => crit.destroy() });
      // Violet aura halo.
      const halo = this.scene.add.circle(ix, iy, 30, 0xd4a0ff, 0.55);
      this.scene.tweens.add({ targets: halo, scale: 1.8, alpha: 0, duration: 380, ease: "Quad.easeOut", onComplete: () => halo.destroy() });
      // Violet shadow shards — more, longer.
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 + Math.random() * 0.4;
        const len = 22 + Math.random() * 16;
        const shard = this.scene.add.rectangle(ix, iy, len, 1.8, 0xd4a0ff, 1);
        shard.setRotation(a);
        this.scene.tweens.add({
          targets: shard,
          x: ix + Math.cos(a) * len * 0.7,
          y: iy + Math.sin(a) * len * 0.7,
          alpha: 0,
          duration: 320 + Math.random() * 140,
          ease: "Quad.easeOut",
          onComplete: () => shard.destroy(),
        });
      }
      return;
    }

    // Void blade — SAW-CUT BURST: a pair of crossed violet slashes, a tight
    // bright-purple shockwave ring, and a fan of spinning blade shards.
    if (this.visualKind === "void_blade") {
      for (const slashAng of [-0.7, 0.7]) {
        const slash = this.scene.add.rectangle(ix, iy, 60, 3.2, 0xffffff, 1).setStrokeStyle(0.6, 0xb84aff, 1);
        slash.setRotation(slashAng);
        this.scene.tweens.add({
          targets: slash, scaleX: 1.8, alpha: 0,
          duration: 320, ease: "Quad.easeOut",
          onComplete: () => slash.destroy(),
        });
      }
      const ring = this.scene.add.circle(ix, iy, 9, 0xb84aff, 0).setStrokeStyle(3, 0xd4a0ff, 0.95);
      this.scene.tweens.add({ targets: ring, scale: 4, alpha: 0, duration: 400, ease: "Cubic.easeOut", onComplete: () => ring.destroy() });
      const crit = this.scene.add.circle(ix, iy, 18, 0xffffff, 1);
      this.scene.tweens.add({ targets: crit, scale: 0.3, alpha: 0, duration: 240, ease: "Quad.easeOut", onComplete: () => crit.destroy() });
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 + Math.random() * 0.4;
        const len = 18 + Math.random() * 14;
        const shard = this.scene.add.rectangle(ix, iy, len, 1.6, 0xd080ff, 1);
        shard.setRotation(a);
        this.scene.tweens.add({
          targets: shard,
          x: ix + Math.cos(a) * len * 0.7,
          y: iy + Math.sin(a) * len * 0.7,
          rotation: a + 1.2,
          alpha: 0,
          duration: 300 + Math.random() * 140,
          ease: "Quad.easeOut",
          onComplete: () => shard.destroy(),
        });
      }
      return;
    }

    // Tinker BOMB DETONATION — massive multi-stage demolition explosion.
    if (this.visualKind === "rocket_barrage") {
      // ── GROUND-BREAKING CRATER (asphalt/cobble cracks, brown palette) ──
      // Only render road-shatter if the impact lands on a road/path tile.
      // Enemies always walk on path cells so this should always be true,
      // but the guard prevents any edge-case crack bleed onto grass.
      const _bGrid = (this.scene as unknown as { grid?: { cells: { x: number; y: number; type: string; col: number; row: number }[][]; cellSize: number } }).grid;
      const _bOnRoad = _bGrid?.cells.some(row => row.some(cell =>
        (cell.type === "path" || cell.type === "spawn" || cell.type === "end") &&
        Math.abs(cell.x - ix) <= _bGrid.cellSize / 2 &&
        Math.abs(cell.y - iy) <= _bGrid.cellSize / 2,
      )) ?? true;
      if (_bOnRoad) this.drawRoadShatter(ix, iy, this.tower.splash * 0.55, 500);  // smaller crater + shorter (user: ground-break too big)
      // Camera punch for weight.
      this.scene.cameras.main.shake(130, 0.0028);
      // Blinding white flash
      const flash = this.scene.add.circle(ix, iy, 32, 0xffffff, 1);
      this.scene.tweens.add({ targets: flash, scale: 0.3, alpha: 0, duration: 220, onComplete: () => flash.destroy() });
      // Massive fireball core
      const fire = this.scene.add.circle(ix, iy, 36, 0xff5a1a, 0.95);
      this.scene.tweens.add({ targets: fire, scale: 2.6, alpha: 0, duration: 540, ease: "Quad.easeOut", onComplete: () => fire.destroy() });
      const fireInner = this.scene.add.circle(ix, iy, 24, 0xffe070, 0.95);
      this.scene.tweens.add({ targets: fireInner, scale: 2.2, alpha: 0, duration: 440, ease: "Quad.easeOut", onComplete: () => fireInner.destroy() });
      // Two earth-toned dust shockwaves — replaces the white/yellow concentric
      // rings (which read as "glass shatter"). Brown dust rings + one inner
      // hot fireball ring sells "ground crater pushing dirt out", not glass.
      const dustRingA = this.scene.add.circle(ix, iy, 10, 0, 0).setStrokeStyle(2.6, 0x8a6a40, 0.78);
      this.scene.tweens.add({
        targets: dustRingA, scale: 3.4, alpha: 0,
        duration: 580, ease: "Cubic.easeOut",
        onComplete: () => dustRingA.destroy(),
      });
      const dustRingB = this.scene.add.circle(ix, iy, 10, 0, 0).setStrokeStyle(2.0, 0x6a4f30, 0.7);
      this.scene.tweens.add({
        targets: dustRingB, scale: 2.9, alpha: 0,
        duration: 540, delay: 90, ease: "Cubic.easeOut",
        onComplete: () => dustRingB.destroy(),
      });
      // Inner orange fireball wave (kept — sells the actual blast).
      const fireWave = this.scene.add.circle(ix, iy, 10, 0, 0).setStrokeStyle(2.4, 0xff6a1a, 0.85);
      this.scene.tweens.add({
        targets: fireWave, scale: 2.4, alpha: 0,
        duration: 360, ease: "Cubic.easeOut",
        onComplete: () => fireWave.destroy(),
      });
      // Mushroom smoke clouds rising
      for (let s = 0; s < 5; s++) {
        const sxx = ix + (Math.random() - 0.5) * 36;
        const syy = iy + (Math.random() - 0.5) * 16;
        const cloud = this.scene.add.circle(sxx, syy, 16 + Math.random() * 8, 0x3a3028, 0.8);
        this.scene.tweens.add({
          targets: cloud,
          x: sxx + (Math.random() - 0.5) * 30,
          y: syy - 18 - Math.random() * 18,
          scale: 2.6, alpha: 0,
          duration: 950 + Math.random() * 320,
          ease: "Quad.easeOut",
          onComplete: () => cloud.destroy(),
        });
      }
      // Heavy fire shrapnel (16 wide-radius sparks)
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2 + Math.random() * 0.3;
        const len = 60 + Math.random() * 50;
        const sp = this.scene.add.circle(ix, iy, 2 + Math.random(), Math.random() < 0.5 ? 0xffe070 : 0xff5a1a, 1);
        this.scene.tweens.add({
          targets: sp,
          x: ix + Math.cos(a) * len,
          y: iy + Math.sin(a) * len,
          alpha: 0, scale: 0.3,
          duration: 620 + Math.random() * 280,
          ease: "Cubic.easeOut",
          onComplete: () => sp.destroy(),
        });
      }
      // Black debris chunks (rocks)
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 + Math.random() * 0.4;
        const len = 40 + Math.random() * 32;
        const chunk = this.scene.add.rectangle(ix, iy, 3 + Math.random() * 2, 3 + Math.random() * 2, 0x1a1a1a, 1);
        chunk.setRotation(Math.random() * Math.PI);
        this.scene.tweens.add({
          targets: chunk,
          x: ix + Math.cos(a) * len,
          y: iy + Math.sin(a) * len + 12,
          rotation: Math.random() * Math.PI * 2,
          alpha: 0,
          duration: 720, ease: "Cubic.easeIn",
          onComplete: () => chunk.destroy(),
        });
      }
      // Heat distortion ground glow
      const ground = this.scene.add.ellipse(ix, iy + 4, 90, 26, 0xff5a1a, 0.55);
      this.scene.tweens.add({ targets: ground, scale: 1.7, alpha: 0, duration: 720, ease: "Quad.easeOut", onComplete: () => ground.destroy() });
      return;
    }

    // Bright impact flash
    const flash = this.scene.add.circle(ix, iy, 12, 0xffffff, 0.9);
    this.scene.tweens.add({
      targets: flash,
      scale: 0.2,
      alpha: 0,
      duration: 180,
      onComplete: () => flash.destroy(),
    });

    // Element-themed core burst
    const core = this.scene.add.circle(ix, iy, 8, color, 0.9);
    this.scene.tweens.add({
      targets: core,
      scale: 2,
      alpha: 0,
      duration: 260,
      ease: "Quad.easeOut",
      onComplete: () => core.destroy(),
    });

    // Spark shrapnel — radial burst of small particles
    const sparkCount = this.tower.splash > 0 ? 12 : 7;
    for (let i = 0; i < sparkCount; i++) {
      const angle = (i / sparkCount) * Math.PI * 2 + Math.random() * 0.5;
      const dist = 14 + Math.random() * 18;
      const s = this.scene.add.circle(ix, iy, 2, color, 1);
      this.scene.tweens.add({
        targets: s,
        x: ix + Math.cos(angle) * dist,
        y: iy + Math.sin(angle) * dist,
        alpha: 0,
        scale: 0.3,
        duration: 350 + Math.random() * 150,
        ease: "Cubic.easeOut",
        onComplete: () => s.destroy(),
      });
    }
  }

  destroy(): void {
    this.alive = false;
    this.visual.destroy();
    if (this.bombShadow) {
      const shadow = this.bombShadow;
      this.bombShadow = undefined;
      // Quick fade out so the shadow doesn't pop off; impact handles its own dust.
      this.scene.tweens.add({
        targets: shadow,
        alpha: 0,
        scale: shadow.scaleX * 1.6,
        duration: 120,
        onComplete: () => shadow.destroy(),
      });
    }
  }
}
